import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Button, Checkbox, IconButton, MenuItem, TextField } from "@mui/material";
import { add } from "date-fns";
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useReactToPrint } from "react-to-print";
import { allOrdersMealsPath, allOrdersPath, allOrdersPricePath, CLASSROOM_CLOSED_RANGE_PATH, CLASSROOMGROUPS_PATH, CLASSROOMS_PATH, COMBOS_PATH, COURSE_TYPES_PATH, dailyMealsPath, DESSERT_DAYS_PATH, DISCOUNT_STAFF_PATH, localOrdersSnacksDatePath, mealsPath, menuPath, MODEL_COMPLEX_PATH, PACKAGES_PATH, PRICE_MEALS_EXTRA_PATH, SCHOOL_BREAKS_PATH, SCHOOL_BREAKS_SUMMER_PATH, SCHOOL_CLOSED_RANGE_PATH, SCHOOL_DATA_PATH, SIDE_DAYS_PATH, SNACK_TYPES_PATH, snacksPath, studentsPath, userOrdersPathDate, userPackagesActive, userProfilePath } from "../../functions/src/admin/paths";
import { KEY_MAX_QUANTITY_LOCAL, PACKAGE_STATUS_COMPLETED, PORTION_SIZE_MEDIUM, PORTION_SIZE_SMALL, SHOW_ORDER_TIME } from "../../functions/src/api/constants";
import { adminOrderDate, adminOrderDateNow, getDateLocale, getOrderFutureDays, getWeekdayName, getWeekDays, toDateFromServer, toDayString } from "../../functions/src/api/dates";
import { isWholeSchoolClosedDay as checkWholeSchoolClosedDay, hasClassroomClosureForDate, resolveClassroomClosure, resolveWholeSchoolClosure, type ClassroomClosedRangeRecord, type SchoolClosedRangeRecord } from "../../functions/src/api/orderClosures";
import { canOrderMenuMealsForDay, isCancelOrChangeBlockedForDay, isDayOrderingUiBlocked, isGeneralOrderDeadlinePassed, orderDeadlinePassedMessage, orderMenuTimeFromModel, validateSetMenuOrderCompletion } from "../../functions/src/common/orderDeadlines";
import { calculateMealLinePrice, calculateSnackLinePrice, isCommittedOrder, normalizeOrderContent, orderHasContentLines, orderStats } from "../../functions/src/common/orders";
import { applyDiscountToPrice, getPackageExchangeMealPrice, getPersonnelPrice } from "../../functions/src/common/pricing";
import { BaseOrderLine, Meal, MealLine, MealsList, ModelComplexity, Order, OrderLine, PriceMealsExtra, SchoolData, Snack, SnackLine, SnacksList, Student, UserMealPackage } from "../../functions/src/types";
import { ANALYTICS_EVENTS, ANALYTICS_LABELS, trackOrderEvent } from "../api/analyticsUtils";
import { useCombosData } from "../api/comboUtils";
import { auth, currentCatering, currentDailyMeals, currentDb, currentMenu, currentSchoolKey, DataSnapshot, mainDatabase } from "../api/firebase";
import { EMPTY_FILTER, useDatabase, useDatabaseVal, useList, useSnapshotState, useStorageUrl } from "../api/firebaseHooks";
import { placeOrder, getCallableErrorMessage, useTopupDiscount } from "../api/funds";
import { getLangValue, langContext, localeFromLang } from "../api/Lang";
import { uppercaseForLang } from "../api/langConfig";
import LoadingSpinner from "../api/LoadingSpinner";
import { getMealData, getMealName, shouldShowPortionSize, useHasMultipleExchangeableMeals } from "../api/mealUtils";
import Modal from "../api/Modal";
import OkDlg from "../api/OkDlg";
import { usePortionSizeLabels } from "../api/portionUtils";
import { formatPriceEuro } from "../api/priceFormat";
import { captureException } from "../api/sentry";
import { useSetDayMenuRangeForDate } from "../api/useSetDayMenuRangesList";
import YesNoDlg from "../api/YesNoDlg";
import { useStrings } from "../common-strings";
import PrintIcon from "../PrintIcon";
import { BaseFood, Lookup, OrderPerson } from "../types";
import Balance from "./Balance";
import CheckoutDeadlineUrgencyAlert from "./basket/CheckoutDeadlineUrgencyAlert";
import { useBasketStrings } from "./basket/basket-strings";
import { useBasketContext } from "./basket/BasketProvider";
import ClosureAttentionIcon from "./ClosureAttentionIcon";
import DaySelector from "./DaySelector";
import QuantitySelect from "./QuantitySelect";
import MealsPopUp from "./MealsPopup";
import { useOrderDeadlineAt } from "./useOrderDeadlineAt";
import OrderInfoBannerSection from "./OrderInfoBannerSection";
import OrderItemRemarkPopUp from "./OrderItemRemarkPopUp";
import OrderPopup, { TabsPopup, TwoButtonPopup } from "./OrderPopup";
import PackageMealsPopUp from "./PackageMealsPopup";
import SetDayMenuPopUp from "./setDayMenuPopUp";
import SnacksPopUp from "./SnacksPopup";
import { endIdleChangeOrderModeForDay, hasUnsubmittedOrderChanges, isChangeOrderEmptyDraft } from "./orderChangingDraft";
import { bindOrderMealsScrollChaining } from "./orderMealsScrollChaining";
import { bindOrderDaySelectorStuckState } from "./orderStickyChrome";
import { bindOrderMealsListCapSyncOnColumnScroll, clearOrderMealsScrollportMaxHeight, getOrderMealsListScrollEl, isWideMealsListBodyScroll, syncOrderMealsScrollLayoutCssVars } from "./orderMealsScrollportLayout";
import { scrollOrderDayContentIntoView } from "./scrollOrderAppContent";
import { useOrderDayDeadlineContext } from "./useOrderDayDeadlineContext";
import { useOrderStrings } from "./order-strings";
import WeeklyOrders from "./WeeklyOrders";

// Finds first date with menu in the future or returns the current date
async function getInitialOrderDate(modelConstants: ModelComplexity, at: Date) {
    if (!modelConstants) {
        return toDayString(adminOrderDateNow(SHOW_ORDER_TIME, at));
    }

    const orderFutureDays = getOrderFutureDays(modelConstants?.orderFutureWeeks);
    let date = toDayString(adminOrderDateNow(SHOW_ORDER_TIME, at));
    for (let i = 0; i < orderFutureDays; i++) {
        // Check if date has menu for this day
        const hasMenu = (await mainDatabase.ref(`${menuPath(currentMenu)}/${date}/menu1`).once('value')).val();
        if (hasMenu)
            return date;
        date = toDayString(adminOrderDate(add(toDateFromServer(date), { days: 1 }), SHOW_ORDER_TIME));
    }
    return toDayString(adminOrderDateNow(SHOW_ORDER_TIME, at));
}

export default function Order({ setMenu }: OrderProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();
    // Multi-day basket checkout: drafts are staged to the basket instead of per-day submit
    const {
        openBasket,
        focusOrderDate,
        clearFocusOrderDate,
        basketLocked,
        checkoutUrgency,
        checkoutUrgencyDismissed,
        dismissCheckoutUrgency,
        modelConstants: basketModelConstants,
        isOpen: basketDrawerOpen,
    } = useBasketContext();
    const showCheckoutUrgency = checkoutUrgency.active && !checkoutUrgencyDismissed && !basketDrawerOpen;
    const basketStrings = useBasketStrings();
    const isWideOrderLayout = useMediaQuery({ minWidth: 931 });
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const deadlineCtx = useOrderDayDeadlineContext();
    const deadlineAt = useOrderDeadlineAt();
    const [pricesExtra] = useDatabaseVal<PriceMealsExtra>(PRICE_MEALS_EXTRA_PATH);
    const { uid } = auth.currentUser;

    const contentRef = useRef(null);
    // Placeholder until Firebase server time loads; ordering stays blocked until deadlineAt is set and getInitialOrderDate runs.
    const [date, setDate] = useState(() => toDayString(adminOrderDateNow(SHOW_ORDER_TIME, new Date())));
    const orderWeekDays = getWeekDays(date);

    /** Set when basket "Edit day" or getInitialOrderDate picks the day — blocks the other from overwriting. */
    const initialDateLockedRef = useRef(false);
    /** One idle/empty change-mode cleanup per day after draft + placed have loaded (refresh / first paint). */
    const changeOrderMountCleanupDoneRef = useRef(false);

    const reactToPrint = useReactToPrint({
        contentRef,
        documentTitle: `Lunchbox Orders ${toDateFromServer(orderWeekDays[0]).toLocaleDateString(localeFromLang(lang))} - ${toDateFromServer(orderWeekDays[4]).toLocaleDateString(localeFromLang(lang))}`
    });

    const scheduleScrollToOrderDayContent = useCallback(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                syncOrderMealsScrollLayoutCssVars({ force: true });
                scrollOrderDayContentIntoView();
            });
        });
    }, []);

    const onOrderDateChange = useCallback((nextDate: string) => {
        setDate(nextDate);
        scheduleScrollToOrderDayContent();
    }, [scheduleScrollToOrderDayContent]);

    // Basket drawer "Edit day" — must run before getInitialOrderDate (async) can overwrite the date
    useEffect(() => {
        if (!focusOrderDate) return;
        initialDateLockedRef.current = true;
        setDate(focusOrderDate);
        clearFocusOrderDate();
        scheduleScrollToOrderDayContent();
    }, [focusOrderDate, clearFocusOrderDate, scheduleScrollToOrderDayContent]);

    // Initialize with first available date when modelConstants and server time are ready
    useEffect(() => {
        if (!modelConstants || !deadlineAt || initialDateLockedRef.current) return;
        getInitialOrderDate(modelConstants, deadlineAt).then(initialDate => {
            if (initialDateLockedRef.current) return;
            initialDateLockedRef.current = true;
            setDate(initialDate);
        }).catch((err) => {
            console.warn('Could not resolve initial order date:', err);
            captureException(err instanceof Error ? err : new Error(String(err)), { context: 'getInitialOrderDate' });
        });
    }, [modelConstants, deadlineAt]);

    const [loadingSpinner, setLoadingSpinner] = useState(false);

    const schoolClosedRanges = useDatabase(SCHOOL_CLOSED_RANGE_PATH || null);
    const schoolClosedRangesList = useList(schoolClosedRanges);
    const classroomClosedRanges = useDatabase(CLASSROOM_CLOSED_RANGE_PATH || null);
    const classroomClosedRangesList = useList(classroomClosedRanges);

    const schoolClosedRangeRecords = useMemo((): SchoolClosedRangeRecord[] => {
        return schoolClosedRangesList?.map(s => s.val() as SchoolClosedRangeRecord) ?? [];
    }, [schoolClosedRangesList]);

    const classroomClosedRangeRecords = useMemo((): ClassroomClosedRangeRecord[] => {
        return classroomClosedRangesList?.map(s => s.val() as ClassroomClosedRangeRecord) ?? [];
    }, [classroomClosedRangesList]);

    const wholeSchoolClosure = useMemo(
        () => resolveWholeSchoolClosure(date, schoolClosedRangeRecords, lang),
        [date, schoolClosedRangeRecords, lang],
    );

    const isWholeSchoolClosedDay = useMemo(
        () => checkWholeSchoolClosedDay(date, schoolClosedRangeRecords),
        [date, schoolClosedRangeRecords],
    );

    const schoolClosedText = wholeSchoolClosure?.closedText;
    const schoolClosedIcon = wholeSchoolClosure?.closedIcon || 'closed.svg';

    const hasClassroomClosureDay = useMemo(
        () => hasClassroomClosureForDate(date, classroomClosedRangeRecords),
        [date, classroomClosedRangeRecords],
    );

    const isAnyClosureDay = isWholeSchoolClosedDay || hasClassroomClosureDay;
    const setDayMenuRange = useSetDayMenuRangeForDate(date);
    const isSetDayMenu = !!setDayMenuRange;
    const setDayPrice = setDayMenuRange?.price ?? null;

    const sSnacks = useDatabase(snacksPath(currentCatering), ref => ref.orderByChild('order'));
    const sSnackTypes = useDatabase(SNACK_TYPES_PATH, ref => ref.orderByChild('order'));
    const snacksList = useList(sSnacks);
    const snackTypes = useList(sSnackTypes);

    // Traditional meals (courseType: main) from meals path
    const sMeals = useDatabase(mealsPath(currentCatering), ref => ref.orderByChild('order'));
    const mealsList = useList(sMeals);

    // Daily meals (all other courseTypes) from dailyMeals path
    const sDailyMeals = useDatabase(dailyMealsPath(currentDailyMeals), ref => ref.orderByChild('order').startAt(0));
    const dailyMealsList: DataSnapshot<Meal>[] = useList(sDailyMeals) || [];

    const sCourseTypes = useDatabase(COURSE_TYPES_PATH, ref => ref.orderByChild('order'));
    const courseTypes = useList(sCourseTypes);

    const [choosingSnack, setChoosingSnack] = useState<{ recipient: string, schoolBreak: string }>(null);
    const [choosingMeal, setChoosingMeal] = useState<{ recipient: string, schoolBreak: string }>(null);

    const placedOrder: DataSnapshot<Order> = useDatabase(date && allOrdersPath(uid, date) || null);
    const [placedPrice, sPlacedPrice] = useDatabaseVal<number>(date && allOrdersPricePath(uid, date) || null);
    const placedOrderMeals = useDatabase(date && allOrdersMealsPath(uid, date) || null);
    const placedOrderMealsList: DataSnapshot<MealLine>[] = useList(placedOrderMeals);
    const hasPlacedOrderRemarks = placedOrder?.exists() && placedOrder?.val().orderRemarks?.length > 0;
    // const totalPackagesPrice = placedOrderMealsList.reduce((prev, pPrice) => prev + (pPrice.val().packagePrice ?? 0), 0);

    const sOrder = useDatabase(date && userOrdersPathDate(uid, date) || null);
    const sOrderSnacks = useMemo(() => sOrder?.child('snacks'), [sOrder]);
    const sOrderMeals = useMemo(() => sOrder?.child('meals'), [sOrder]);
    const OrderSnacksLists = useList(sOrderSnacks);
    const OrderMealsLists: DataSnapshot<MealLine>[] = useList(sOrderMeals);
    const WholeOrderLists = [...OrderMealsLists, ...OrderSnacksLists];

    const menusSnapshot = useDatabase(`${menuPath(currentMenu)}/${date}`);
    const menu1Key: string = menusSnapshot?.child('menu1/key')?.val();
    const sideDish: Meal = menusSnapshot?.child('sideMenu')?.val();
    const dessert: Meal = menusSnapshot?.child('dessertMenu')?.val();

    // Get menu items for the date to use correct pricing for menu items
    const menuItems = useList(menusSnapshot);
    const menuData: { [key: string]: Meal } = {};

    if (menuItems) {
        menuItems.forEach((menuItemSnapshot) => {
            const menuItem = menuItemSnapshot.val();
            if (menuItem && menuItem.key) {
                // Use the meal key as the key for menuData
                menuData[menuItem.key] = menuItem;
            }
        });
    }

    const sProfile = useDatabase(userProfilePath(uid));
    const staffDiscount: number = useDatabase(DISCOUNT_STAFF_PATH)?.val() || 0;
    const [combosData] = useDatabaseVal(COMBOS_PATH);
    const orderPeople = useDatabase(studentsPath(uid));
    const orderPeopleList: DataSnapshot<OrderPerson>[] = useList(orderPeople);
    const sSchoolBreaksSummer = useDatabase(SCHOOL_BREAKS_SUMMER_PATH);
    const sSchoolBreaksRegular = useDatabase(SCHOOL_BREAKS_PATH);
    const sSchoolBreaks = isSetDayMenu ? sSchoolBreaksSummer : sSchoolBreaksRegular;

    // Get classroom data for sorting
    const classrooms = useDatabase(CLASSROOMS_PATH);
    const classroomGroups = useDatabase(CLASSROOMGROUPS_PATH);
    const classroomsList = useList(classrooms);
    const classroomGroupsList = useList(classroomGroups);

    // Get top-up discount
    const topupDiscount = useTopupDiscount();
    const discountPercentage = topupDiscount?.discountPercentage || 0;

    // Create sorted list: students first (sorted by classroom), then personnel
    const sortedStudentsList = useMemo(() => {
        if (!orderPeopleList || !classroomsList || !classroomGroupsList) return orderPeopleList;

        return orderPeopleList.sort((a, b) => {
            const personA = a.val() as OrderPerson;
            const personB = b.val() as OrderPerson;

            // Check if they are students or personnel
            const isStudentA = !!personA?.student;
            const isStudentB = !!personB?.student;

            // Personnel should come after students
            if (isStudentA && !isStudentB) return -1;
            if (!isStudentA && isStudentB) return 1;

            // If both are personnel, sort by name
            if (!isStudentA && !isStudentB) {
                const nameA = `${personA.firstName} ${personA.lastName}`;
                const nameB = `${personB.firstName} ${personB.lastName}`;
                return nameA.localeCompare(nameB);
            }

            // If both are students, sort by classroom
            const studentA = personA as Student;
            const studentB = personB as Student;
            const classroomA = studentA?.classroom;
            const classroomB = studentB?.classroom;

            if (!classroomA || !classroomB) return 0;

            // Get classroom data
            const classroomDataA = classroomsList.find(c => c.key === classroomA);
            const classroomDataB = classroomsList.find(c => c.key === classroomB);

            if (!classroomDataA || !classroomDataB) return 0;

            const classroomGroupA = classroomDataA.val()?.classroomGroup;
            const classroomGroupB = classroomDataB.val()?.classroomGroup;

            // Get classroom group order
            const groupDataA = classroomGroupsList.find(g => g.key === String(classroomGroupA));
            const groupDataB = classroomGroupsList.find(g => g.key === String(classroomGroupB));

            const groupOrderA = groupDataA?.val()?.order || 0;
            const groupOrderB = groupDataB?.val()?.order || 0;

            // First sort by group order
            if (groupOrderA !== groupOrderB) {
                return groupOrderA - groupOrderB;
            }

            // Then sort by classroom name within the same group
            const classroomNameA = classroomDataA.val()?.name?.el || classroomA;
            const classroomNameB = classroomDataB.val()?.name?.el || classroomB;

            // Custom sorting for Greek classroom names (e.g., "Α1", "Β2", etc.)
            const matchA = classroomNameA.match(/^([α-ωΑ-Ωa-zA-Z]+)(\d+)$/);
            const matchB = classroomNameB.match(/^([α-ωΑ-Ωa-zA-Z]+)(\d+)$/);

            if (matchA && matchB) {
                const [, letterA, numA] = matchA;
                const [, letterB, numB] = matchB;

                // First compare letters
                if (letterA !== letterB) {
                    return letterA.localeCompare(letterB);
                }

                // Then compare numbers numerically
                return parseInt(numA) - parseInt(numB);
            }

            // Fallback to regular string comparison
            return classroomNameA.localeCompare(classroomNameB);
        });
    }, [orderPeopleList, classroomsList, classroomGroupsList]);

    // If role is personnel - filter orders for staff members
    const adultOrdersAll = WholeOrderLists.filter(orderList => {
        const recipientId = orderList.child('recipient').val();
        const recipientData = orderPeople?.child(recipientId)?.val();
        return !!recipientData?.personnel;
    });
    const adultOrders = adultOrdersAll?.filter(ao => !ao.child('isPackage').val());
    const adultOrdersPackage = adultOrdersAll?.filter(ao => !!ao.child('isPackage').val());
    // const packageMeals = OrderMealsLists.filter(oml => oml.child('isPackage').val());
    const hasPackageInOrder = OrderMealsLists?.length > 0 && OrderMealsLists.some(oml => !!oml.child('isPackage').val());

    const [orderRemarks, setOrderRemarks] = useSnapshotState<string>(sOrder, 'orderRemarks');
    const hasOrderRemark = !!orderRemarks;
    const orderRemarksPlaceholder = orderStrings.orderRemarksPlaceholder;

    const hasPlacedPricedOrder = (typeof placedPrice === "number") && placedPrice > 0;
    const isChangingOrder = sOrder?.child('isChangingOrder')?.val() === true;
    const hasPlacedOrder = !isChangingOrder && (typeof placedPrice === "number") && (placedPrice > 0 || (placedPrice === 0 && !!placedOrderMealsList && placedOrderMealsList.length > 0 && placedOrderMealsList.some(pm => (pm?.val()?.key != menu1Key) || (!!pm?.val()?.orderItemRemark || hasPlacedOrderRemarks))));
    const hasPlacedOrderOrPackage = placedOrder?.exists();
    const hasPlacedPackageOrderForDay = !!placedOrderMealsList?.some(pm => !!pm.child('isPackage').val());
    /** Registered order with lines to show — not a bare/cancelled allOrders shell (price -1 or empty). */
    const hasActivePlacedOrderForDay = isCommittedOrder(placedOrder?.val()) && (hasPlacedOrder || hasPlacedPackageOrderForDay);
    const hasPlacedNormalOrderContent = !!placedOrderMealsList?.some(pm => !pm.child('isPackage').val()) || Object.keys(placedOrder?.val()?.snacks ?? {}).length > 0;
    const draftDiffersFromPlaced = useMemo(() => {
        const placed = placedOrder?.val();
        const draft = sOrder?.val();
        if (!placed || !draft || !isCommittedOrder(placed)) {
            return false;
        }
        return normalizeOrderContent(draft) !== normalizeOrderContent(placed);
    }, [sOrder, placedOrder]);

    const isPast = !!deadlineAt && isGeneralOrderDeadlinePassed(date, modelConstants, deadlineAt);
    const cannotOrder = !deadlineAt || (deadlineCtx ? isDayOrderingUiBlocked(date, deadlineCtx, isChangingOrder, deadlineAt) : true);

    // Cancel and Change buttons should only be disabled when the order deadline has passed (isPast)
    // Change order can include cancellation of individual items, so it follows the same rules as cancel order
    const cannotCancelOrder = isPast;

    const getClassroomOnlyClosureForStudent = (classroomKey: string | null | undefined) => {
        if (isWholeSchoolClosedDay) return null;
        return resolveClassroomClosure(date, classroomKey, classroomClosedRangeRecords, lang, false);
    };

    const recipientsWithPlacedNormalContent = useMemo(() => {
        const keys = new Set<string>();
        placedOrderMealsList?.forEach(pm => {
            if (pm.child('isPackage').val()) return;
            const recipientKey = pm.child('recipient').val();
            if (recipientKey) keys.add(recipientKey);
        });
        Object.values(placedOrder?.val()?.snacks ?? {}).forEach((snack: SnackLine) => {
            if (snack.recipient) keys.add(snack.recipient);
        });
        return keys;
    }, [placedOrderMealsList, placedOrder]);

    const isSchoolClosedDay = isWholeSchoolClosedDay;
    const disableChangeMyOrderSchoolClosed = useMemo(() => {
        if (!hasPlacedOrder || !hasPlacedNormalOrderContent) {
            return false;
        }
        if (isWholeSchoolClosedDay) {
            return true;
        }
        const hasOpenStudent = orderPeopleList?.some(sPerson => {
            const person = sPerson.val();
            if (!(person as Student)?.student) return false;
            return !getClassroomOnlyClosureForStudent((person as Student).classroom);
        });
        return !hasOpenStudent;
    }, [hasPlacedOrder, hasPlacedNormalOrderContent, isWholeSchoolClosedDay, orderPeopleList, date, classroomClosedRangeRecords, lang, wholeSchoolClosure]);

    useEffect(() => {
        if (hasPlacedOrderOrPackage && sOrder?.ref && placedOrder?.val()) {
            const placedOrderData = placedOrder.val();

            // Basket drafts persist across date navigation until checkout — do not reset
            // pending change-order edits when revisiting a day.
            const draftData = sOrder.val();
            if (draftData?.isChangingOrder) return;
            if (draftData && orderHasContentLines(draftData) && normalizeOrderContent(draftData) !== normalizeOrderContent(placedOrderData)) {
                return;
            }
            // Do not repopulate an empty draft from placed (e.g. after removeOrderFromUser during cancel — would re-enter the basket)
            if (!draftData || !orderHasContentLines(draftData)) {
                return;
            }

            // Don't copy orders that only contain package meals - they're automatically managed
            const hasNonPackageMeals = Object.values(placedOrderData?.meals || {}).some((meal: any) => !meal.isPackage);
            const hasSnacks = Object.keys(placedOrderData?.snacks || {}).length > 0;
            const hasRemarks = !!placedOrderData?.orderRemarks;

            if (hasNonPackageMeals || hasSnacks || hasRemarks) {
                sOrder.ref.set(placedOrderData);
            }
        }
    }, [date, sOrder, placedOrder, hasPlacedOrderOrPackage]);

    // If no order is given, show next day instead of current day
    // useEffect(() => {
    //     if (!hasPlacedOrderOrPackage)
    //         setDate(toDayString(new Date(adminOrderDate(0))));
    // }, []);

    const schoolBreaks: string[] = Object.keys(adultOrders?.reduce((prev, orderLine) => {
        prev[orderLine.child('schoolBreak').val()] = 1;
        return prev;
    }, {} as { [key: string]: 1 }) || {});

    const userPackages = useDatabase(userPackagesActive(uid) || null);
    const userPackagesList: DataSnapshot<UserMealPackage>[] = useList(userPackages).filter(l => l.child('status').val() === PACKAGE_STATUS_COMPLETED);
    const sMealPackages = useDatabase(PACKAGES_PATH);
    const packageRecipientsInPlacedOrder = useMemo(() => {
        const recipients = new Set<string>();
        placedOrderMealsList?.forEach(meal => {
            if (meal.child('isPackage').val() === true) {
                const recipient = meal.child('recipient').val();
                if (recipient) {
                    recipients.add(recipient);
                }
            }
        });
        return recipients;
    }, [placedOrderMealsList]);
    const hasPackageWithShowOrderOnClosed = useMemo(() => {
        if (packageRecipientsInPlacedOrder.size === 0) {
            return false;
        }
        return userPackagesList?.some(userPackage => {
            const packageId = userPackage.val().packageId;
            if (!packageId || sMealPackages?.child(packageId).child('showOrderOnSchoolClosed').val() !== true) {
                return false;
            }
            const recipient = userPackage.child('recipient').val();
            if (!recipient || !packageRecipientsInPlacedOrder.has(recipient)) {
                return false;
            }
            const student = orderPeople?.child(recipient)?.val() as Student | undefined;
            const recipientClosed = isWholeSchoolClosedDay
                || (student?.student && !!getClassroomOnlyClosureForStudent(student.classroom));
            if (!recipientClosed) {
                return false;
            }
            const start = userPackage.child('start').val();
            const end = userPackage.child('end').val();
            if (!start || !end) {
                return false;
            }
            return toDateFromServer(date) >= toDateFromServer(start) && toDateFromServer(date) <= toDateFromServer(end);
        }) || false;
    }, [date, isWholeSchoolClosedDay, classroomClosedRangeRecords, packageRecipientsInPlacedOrder, sMealPackages, userPackagesList, orderPeople, lang]);
    const hasOrderPeopleWithoutPackage = useMemo(() => {
        if (!orderPeopleList?.length) {
            return false;
        }
        const recipientsWithPackage = new Set(userPackagesList?.map(userPackage => userPackage.child('recipient').val()).filter(Boolean) || []);
        return orderPeopleList.some(person => !recipientsWithPackage.has(person.key));
    }, [orderPeopleList, userPackagesList]);
    const disableWholeFormSchoolClosedPackageDay = isSchoolClosedDay && hasPackageWithShowOrderOnClosed && hasPlacedPackageOrderForDay;
    const isFormDisabled = cannotOrder || (!!hasPlacedOrder && !draftDiffersFromPlaced && !isChangingOrder) || disableWholeFormSchoolClosedPackageDay || basketLocked;

    // Filter sortedStudentsList to only show users with packages in packages only mode
    const filteredStudentsList = useMemo(() => {
        if (!modelConstants?.packagesOnly) {
            return sortedStudentsList;
        }

        // In packages only mode, filter out users who don't have packages
        return sortedStudentsList.filter(person => {
            const personKey = person.key;
            return userPackagesList?.some(packageSnapshot =>
                packageSnapshot.child('recipient').val() === personKey
            ) || false;
        });
    }, [sortedStudentsList, modelConstants?.packagesOnly, userPackagesList]);

    const [addingOrder, setAddingOrder] = useState(null);
    const onAddingOrder = (recipient: string) => {
        setAddingOrder(recipient);
    }

    const validBreaks = useList(sSchoolBreaks);
    const hasEmptyBreak = (recipient: string) => {
        return validBreaks.some(br => {
            for (const o of WholeOrderLists) {
                if (o.child('recipient').val() === recipient) {
                    if (o.child('schoolBreak').val() === (br.key)) {
                        return false;
                    }
                }
            }
            return true;
        });
    }

    const onAddSnack = (recipient: string) => {
        sOrder?.ref?.child('snacks').push({ created: Date.now(), recipient: recipient }).onDisconnect().update({ created: null });
    }



    const hasActiveOrder = (student: string) => {

        return OrderSnacksLists.some(osl => osl.child('recipient').val() === student) || OrderMealsLists.some(oml => oml.child('recipient').val() === student);
    }

    function onSnackPopupClosed() {

        const hasFood = WholeOrderLists.filter(list => {
            return list.child('recipient').val() === choosingSnack.recipient && list.child('schoolBreak').val() === choosingSnack.schoolBreak;
        }).length > 0;

        if (choosingSnack.recipient === addingOrder && hasFood)
            setAddingOrder(null)
        setChoosingSnack(null);
    }

    function onMealPopupClosed() {

        const hasFood = WholeOrderLists.filter(list => {
            return list.child('recipient').val() === choosingMeal.recipient && list.child('schoolBreak').val() === choosingMeal.schoolBreak;
        }).length > 0;

        if (choosingMeal.recipient === addingOrder && hasFood)
            setAddingOrder(null)
        setChoosingMeal(null);
    }

    function onSetDayMenuPopupClosed() {
        // Clear all order items for the current date, recipient, and school break
        if (choosingMeal && sOrderMeals?.ref) {
            const { recipient, schoolBreak } = choosingMeal;

            // Remove all meal orders for this recipient and school break
            OrderMealsLists.forEach(orderMeal => {
                const orderRecipient = orderMeal.child('recipient').val();
                const orderSchoolBreak = orderMeal.child('schoolBreak').val();

                if (orderRecipient === recipient && orderSchoolBreak === schoolBreak) {
                    orderMeal.ref.remove();
                }
            });
        }

        // Close the popup
        setChoosingMeal(null);
    }

    const [ordering, setOrdering] = useState(false);
    const [orderComplete, setOrderComplete] = useState(false); // Controls whether to show the order completion/cancellation page
    const [orderCompleteIsCompletion, setOrderCompleteIsCompletion] = useState<boolean | null>(null); // Tracks whether the completed order is a completion (true) or cancellation (false)

    // Check if current order contains set menu meals (meals with courseType: main)
    const hasSetMenuMealsInOrder = useMemo(() => {
        if (!placedOrderMealsList || placedOrderMealsList.length === 0) return false;

        return placedOrderMealsList.some(mealSnapshot => {
            const mealKey = mealSnapshot.val()?.key;
            const isPackage = mealSnapshot.child('isPackage')?.val();
            if (!mealKey || isPackage) return false;

            // Check if this meal key exists in mealsList (set menu meals) and has courseType: main
            const setMenuMeal = mealsList?.find(meal => meal.key === mealKey);
            return setMenuMeal?.val()?.courseType === 'main';
        });
    }, [placedOrderMealsList, mealsList]);

    // Check if set menu ordering deadline has passed
    const setMenuDeadlinePassed = !deadlineAt || !canOrderMenuMealsForDay(date, modelConstants, deadlineAt);

    // Create warning message for set menu meals
    const getSetMenuWarningMessage = () => {
        if (!hasSetMenuMealsInOrder || !setMenuDeadlinePassed) return '';

        return orderStrings.setMenuWarningMessage;
    };

    const [dlgCancelOrder, setDlgCancelOrder] = useState(false);
    const [orderActionErrorMsg, setOrderActionErrorMsg] = useState<string | null>(null);
    const [changingOrder, setChangingOrder] = useState(false);

    const onCompleteOrder = async (isOrderCompletion: boolean) => {

        if (ordering)
            return;

        if (!sOrder?.ref) {
            console.log('Order reference not available');
            return;
        }

        setLoadingSpinner(true);
        setOrdering(true);

        try {
            await sOrder.ref.child('isChangingOrder').remove();
            const order: Order = (await sOrder.ref.once('value')).val();   // read order again

            if (isOrderCompletion) {
                // Track Complete Order button press with Google Analytics
                trackOrderEvent(
                    ANALYTICS_EVENTS.COMPLETE_ORDER_CLICKED,
                    isChangingOrder ? ANALYTICS_LABELS.ORDER_CHANGE : ANALYTICS_LABELS.NEW_ORDER,
                    currentSchoolKey || 'unknown',
                    hasPackageInOrder ? 'package' : 'normal',
                    uid
                );

                if (deadlineCtx && deadlineAt) {
                    const validation = validateSetMenuOrderCompletion(date, order, placedOrder?.val() ?? null, deadlineCtx, deadlineAt);
                    if (!validation.ok) {
                        sOrderMeals?.ref?.set(placedOrderMeals?.val());
                        setOrderActionErrorMsg(orderStrings.cannotPlaceSetMenuForDate);
                        return;
                    }
                }
            }
            const userDate = Date.now();

            try {
                const result = await placeOrder({ date, order, userDate, isCancelOrder: !isOrderCompletion, localDbURL: currentDb.databaseURL, clientLang: lang });

                if (result.error) {
                    sOrderMeals?.ref?.set(placedOrderMeals?.val());
                    setOrderActionErrorMsg(result.error);
                } else {
                    if (isOrderCompletion) {
                        // Track successful order completion with Google Analytics
                        trackOrderEvent(
                            ANALYTICS_EVENTS.ORDER_COMPLETED_SUCCESSFULLY,
                            isChangingOrder ? ANALYTICS_LABELS.ORDER_CHANGE_COMPLETED : ANALYTICS_LABELS.NEW_ORDER_COMPLETED,
                            currentSchoolKey || 'unknown',
                            hasPackageInOrder ? 'package' : 'normal',
                            uid
                        );
                    } else {
                        // Track successful order cancellation with Google Analytics
                        trackOrderEvent(
                            ANALYTICS_EVENTS.ORDER_CANCELLED_SUCCESSFULLY,
                            ANALYTICS_LABELS.ORDER_CANCEL_COMPLETED,
                            currentSchoolKey || 'unknown',
                            hasPackageInOrder ? 'package' : 'normal',
                            uid
                        );
                        const placedAfterCancel = (await placedOrder?.ref?.once('value'))?.val();
                        if (placedAfterCancel && isCommittedOrder(placedAfterCancel)) {
                            await sOrder.ref.set(placedAfterCancel);
                        } else {
                            await sOrder.ref.remove();
                        }
                    }

                    if (changingOrder)
                        setChangingOrder(false);
                    // Store the change status from the result and show completion page
                    setOrderCompleteIsCompletion(isOrderCompletion);
                    setOrderComplete(true);
                }
            } catch (error) {
                if (isOrderCompletion) {
                    // Track order completion error with Google Analytics
                    trackOrderEvent(
                        ANALYTICS_EVENTS.ORDER_COMPLETION_ERROR,
                        isChangingOrder ? ANALYTICS_LABELS.ORDER_CHANGE_ERROR : ANALYTICS_LABELS.NEW_ORDER_ERROR,
                        currentSchoolKey || 'unknown',
                        hasPackageInOrder ? 'package' : 'normal',
                        uid
                    );
                } else {
                    // Track order cancellation error with Google Analytics
                    trackOrderEvent(
                        ANALYTICS_EVENTS.ORDER_CANCELLATION_ERROR,
                        ANALYTICS_LABELS.ORDER_CANCEL_ERROR,
                        currentSchoolKey || 'unknown',
                        hasPackageInOrder ? 'package' : 'normal',
                        uid
                    );
                }

                // Handle Firebase errors
                console.error('Error placing order:', error);
                sOrderMeals?.ref?.set(placedOrderMeals?.val());

                let errorMessage = getCallableErrorMessage(error, orderStrings.placeOrderFailedTryAgain);
                setOrderActionErrorMsg(errorMessage);
            }
        } catch (error) {
            console.error('Error preparing or reading order before placeOrder:', error);
            captureException(error instanceof Error ? error : new Error(String(error)), { context: 'onCompleteOrder_prepare' });
            setOrderActionErrorMsg(getCallableErrorMessage(error, orderStrings.placeOrderFailedTryAgain));
        } finally {
            setOrdering(false);
            setLoadingSpinner(false);
        }

    }

    const removeOrderFromUser = async () => {

        if (hasPackageInOrder) {
            // remove snacks and order params; keep pkgCopy_* slots — update in place (no push)
            await sOrderSnacks?.ref?.remove();

            for (const mealD of OrderMealsLists ?? []) {
                const meal = mealD?.val();
                const mealId = mealD?.key;
                if (!meal || !mealId) continue;
                // If order has a prepaid package, copy the menu 1 in the order
                if (meal.isPackage) {
                    await sOrderMeals?.ref?.child(mealId).update({
                        packagePrice: 0,
                        key: menu1Key,
                        orderItemRemark: null,
                    });
                } else {
                    // remove meal
                    await sOrderMeals?.ref?.child(mealId).remove();
                }
            }
            await sOrder?.ref?.update({ price: null, timestamp: null, uid: null, orderRemarks: null });
        }
        else
            await sOrder?.ref?.remove();
    }

    const cancelorder = async () => {

        if (basketLocked) return;

        // Track Cancel Order button press with Google Analytics
        trackOrderEvent(
            ANALYTICS_EVENTS.CANCEL_ORDER_CLICKED,
            ANALYTICS_LABELS.CANCEL_ORDER,
            currentSchoolKey || 'unknown',
            hasPackageInOrder ? 'package' : 'normal',
            uid
        );

        if (!deadlineAt || isCancelOrChangeBlockedForDay(date, modelConstants, deadlineAt)) {
            setDlgCancelOrder(false);
            setOrderActionErrorMsg(orderDeadlinePassedMessage(lang));
            return;
        }

        setDlgCancelOrder(false);

        setLoadingSpinner(true);
        try {
            await removeOrderFromUser();
        } catch (error) {
            console.error('Error removing draft order before cancel:', error);
            captureException(error instanceof Error ? error : new Error(String(error)), { context: 'cancelorder_removeOrderFromUser' });
            setLoadingSpinner(false);
            setOrderActionErrorMsg(getCallableErrorMessage(error, orderStrings.placeOrderFailedTryAgain));
            return;
        }
        setLoadingSpinner(false);

        try {
            await onCompleteOrder(false);
        } catch (error) {
            console.error('Error completing order cancellation:', error);
            captureException(error instanceof Error ? error : new Error(String(error)), { context: 'cancelorder_onCompleteOrder' });
        }
    }

    const changeOrder = async () => {
        if (basketLocked) return;
        setLoadingSpinner(false);
        setChangingOrder(true);
        try {
            const placedOrder = (await sPlacedPrice?.ref?.parent?.once('value'))?.val();
            await sOrder?.ref?.set(placedOrder);
            await sOrder?.ref?.child('isChangingOrder')?.set(true);
        } catch (error) {
            console.error('Error starting order change:', error);
            captureException(error instanceof Error ? error : new Error(String(error)), { context: 'changeOrder' });
            setChangingOrder(false);
        }
    }

    const cancelChangeOrder = async () => {
        if (basketLocked) return;
        try {
            await sOrder?.ref?.child('isChangingOrder')?.remove();
            await sOrder?.ref?.set(placedOrder?.val());
        } catch (error) {
            console.error('Error reverting order change:', error);
            captureException(error instanceof Error ? error : new Error(String(error)), { context: 'cancelChangeOrder' });
        }
    }

    // Change mode with no valid staged draft (no edits or empty draft): exit when leaving the day or screen.
    useEffect(() => {
        const dayKey = date;
        return () => {
            endIdleChangeOrderModeForDay(uid, dayKey).catch((error) => {
                console.error('Error ending idle change order mode:', error);
                captureException(error instanceof Error ? error : new Error(String(error)), { context: 'endIdleChangeOrderModeForDay' });
            });
        };
    }, [date, uid]);

    useEffect(() => {
        changeOrderMountCleanupDoneRef.current = false;
    }, [date]);

    useEffect(() => {
        if (!uid || !date || sOrder === undefined || placedOrder === undefined) return;
        if (changeOrderMountCleanupDoneRef.current) return;
        changeOrderMountCleanupDoneRef.current = true;

        endIdleChangeOrderModeForDay(uid, date).catch((error) => {
            console.error('Error ending idle change order mode on load:', error);
            captureException(error instanceof Error ? error : new Error(String(error)), { context: 'endIdleChangeOrderModeForDayOnLoad' });
        });
    }, [uid, date, sOrder, placedOrder]);

    // Check if essential data is loaded
    const essentialDataLoaded = sProfile && sSchoolBreaks;

    // Check if optional data is loaded (these can be null if not configured)
    const optionalDataLoaded = {
        snacks: sSnacks !== undefined, // undefined = still loading, null = not configured
        snackTypes: sSnackTypes !== undefined,
        userOrders: sOrder !== undefined,
        placedOrders: placedOrder !== undefined
    };

    // Only render when essential data is loaded
    const isReady = essentialDataLoaded;

    // Log warnings for missing optional data
    if (isReady && !optionalDataLoaded.snacks) {
        console.warn('Snacks data not loaded - snack ordering may not be available');
    }
    if (isReady && !optionalDataLoaded.snackTypes) {
        console.warn('Snack types data not loaded - snack categorization may not work');
    }
    if (isReady && !optionalDataLoaded.userOrders) {
        console.warn('User draft orders data not loaded - order management may not work');
    }
    if (isReady && !optionalDataLoaded.placedOrders) {
        console.warn('User placed orders data not loaded - order history may not work');
    }

    // Calculate the total setDayPrice by counting unique recipients ordering
    const totalSetDayPrice = useMemo(() => {
        if (!setDayPrice || !isSetDayMenu) return null;

        // Get unique recipients who have orders
        const uniqueRecipients = new Set();
        WholeOrderLists?.forEach(orderList => {
            const recipient = orderList.child('recipient').val();
            if (recipient) {
                uniqueRecipients.add(recipient);
            }
        });

        return setDayPrice * uniqueRecipients.size;
    }, [setDayPrice, isSetDayMenu, WholeOrderLists]);

    const { totalPrice = 0, itemsCount = 0 } = useMemo(() => {
        // Build recipients map from students list
        const recipients: { [key: string]: { personnel?: boolean, student?: boolean } } = {};
        filteredStudentsList.forEach(student => {
            const person = student.val() as OrderPerson;
            if (person) {
                recipients[student.key] = {
                    personnel: person.personnel,
                    student: person.student
                };
            }
        });

        return orderStats(uid, sOrder?.val(), sSnacks?.val(), sMeals?.val(), sDailyMeals?.val(), recipients, staffDiscount, pricesExtra, sideDish, dessert, totalSetDayPrice, combosData, discountPercentage, menuData, !!modelConstants?.chargePackageExchangePrice) ?? { totalPrice: 0, itemsCount: 0 };
    }, [sOrder, sSnacks, sMeals, sDailyMeals, filteredStudentsList, staffDiscount, pricesExtra, sideDish, dessert, totalSetDayPrice, combosData, discountPercentage, date, menuData, modelConstants]);

    // With basket checkout the user pays at checkout (credits + Viva) — no upfront balance gate

    const recipientMissing = Object.values(sOrder?.child('snacks')?.val() ?? sOrder?.child('meals')?.val() ?? {}).reduce((prev, line: OrderLine) => prev || (line?.quantity && !line?.recipient), false);

    const orderOtherThanPackage = hasOrderRemark || OrderSnacksLists?.length > 0 || OrderMealsLists?.filter(order => !order?.child('isPackage')?.val() || order?.val()?.key != menu1Key || !!order?.val()?.orderItemRemark)?.length > 0;
    const isEditingOrder = orderOtherThanPackage && (!hasPlacedOrder || isChangingOrder || draftDiffersFromPlaced);

    const changingOrderHasUnsubmittedChanges = useMemo(
        () => hasUnsubmittedOrderChanges(isChangingOrder, sOrder?.val(), placedOrder?.val()),
        [isChangingOrder, sOrder, placedOrder],
    );
    const changeOrderIsEmptyDraft = useMemo(
        () => isChangeOrderEmptyDraft(isChangingOrder, sOrder?.val(), placedOrder?.val()),
        [isChangingOrder, sOrder, placedOrder],
    );

    // Check if there are multiple exchangeable meals available using shared utility
    const hasMultipleExchangeableMeals = useHasMultipleExchangeableMeals(date);

    // Hide exchange button in ANY mode if there are no exchange options available
    const shouldHideExchangeButton = !hasMultipleExchangeableMeals;

    // Hide Complete Order button only in packages only mode if there are no exchange options available
    // In normal mode, show it because users can order outside the package
    const shouldHideCompleteOrderButton = modelConstants?.packagesOnly && !hasMultipleExchangeableMeals;

    const orderValid = (() => {
        if (!totalPrice && !hasPackageInOrder)    // Empty order
            return false;

        if (recipientMissing)
            return false;

        if (isChangingOrder) {
            if (changeOrderIsEmptyDraft) {
                return false;
            }
            return changingOrderHasUnsubmittedChanges;
        }

        // Only enable button when there are actual changes to submit (same logic as "Awaiting submission")
        if (!isEditingOrder) {
            return false;
        }

        return true;
    })();

    // The per-day submit is "View basket" — drafts are already staged there
    // and are paid all together from the drawer (incl. €0 package meal exchanges).
    const onSubmitOrder = useCallback(() => {
        if (basketLocked) return;
        openBasket();
    }, [basketLocked, openBasket]);
    const submitOrderLabel = basketStrings.viewBasket;
    const sendChangesLabel = basketStrings.viewBasket;

    const snackSectionTitles: string[] = [];

    // useEffect(() => {
    snackTypes?.filter(s_type => s_type?.child('order')?.val() > 0)?.map(sType => {
        if (snacksList?.some(s_snack => s_snack?.child('snackType')?.val() === sType?.key))
            snackSectionTitles.push(sType?.child(lang)?.val());
    });
    // }, [snackTypes]);
    // console.log(snackSectionTitles);

    const ordersTextDoubleLayout = useMemo(() => {
        if (!modelConstants?.cancelOrderDoubleLayout) {
            return false;
        }
        const generalTime = modelConstants.orderTime ?? 22;
        const menuTime = orderMenuTimeFromModel(modelConstants);
        return modelConstants.orderDays !== modelConstants.orderMenuDays || generalTime !== menuTime;
    }, [modelConstants?.orderDays, modelConstants?.orderMenuDays, modelConstants?.orderTime, modelConstants?.orderMenuTime, modelConstants?.cancelOrderDoubleLayout]);


    // Function to get day name based on orderMenuSpecificDay (0=Sunday, 1=Monday, etc.)
    const getDayName = (dayNumber: number | undefined) => {
        if (dayNumber === undefined || dayNumber === null) return orderStrings.fallbackOrderMenuWeekday;
        return getWeekdayName(dayNumber, lang);
    };

    // *******
    // Texts for single layout - orders and cancellations at the same time
    // *******

    // Function to get single layout text
    const getSingleLayoutText = () => {
        const menuTime = orderMenuTimeFromModel(modelConstants);

        if (modelConstants?.orderDays == null && modelConstants?.orderMenuSpecificDay >= 0) {
            return <>
                {orderStrings.singleOrdersUntilWeekdayPrefix}
                <span style={{ fontWeight: 'bold' }}>
                    {getDayName(modelConstants?.orderMenuSpecificDay)} {menuTime}:00
                </span>
                {orderStrings.singleTheWeekBeforeSuffix}
            </>
        }
        else if (modelConstants?.orderDays === 0) {
            return <>
                {orderStrings.singleOrdersUntilTimePrefix}
                <span style={{ fontWeight: 'bold' }}>{modelConstants?.orderTime}:00</span>
                {orderStrings.singleSameDayNoRefundSuffix}
            </>;
        } else if (modelConstants?.orderDays === 1) {
            return <>
                {orderStrings.singleOrdersUntilTimePrefix}
                <span style={{ fontWeight: 'bold' }}>{modelConstants?.orderTime}:00</span>
                {orderStrings.singlePrevDayNoRefundSuffix}
            </>;
        } else {
            return <>
                {orderStrings.singleOrdersUntilTimePrefix}
                <span style={{ fontWeight: 'bold' }}>{modelConstants?.orderTime}:00, {modelConstants?.orderDays}</span>
                {orderStrings.singleNDaysNoRefundSuffix}
            </>;
        }
    };

    // *******
    // Texts for double layout
    // *******

    // Calculate correct set menus count by excluding side dishes and desserts
    const actualSetMenusCount = useMemo(() => {
        if (!menuItems) return modelConstants?.maxSetMenus || 0;

        const sideDishCount = menuItems.filter(menuItem => {
            const item = menuItem.val();
            return item?.isSide === true || false;
        }).length;

        const dessertCount = menuItems.filter(menuItem => {
            const item = menuItem.val();
            return item?.isDessert === true || false;
        }).length;

        return (modelConstants?.maxSetMenus || 0) - sideDishCount - dessertCount;
    }, [menuItems, modelConstants?.maxSetMenus]);

    // Function to get double layout orders text
    const getDoubleLayoutOrdersText = () => {
        const orderDays = modelConstants?.orderDays;
        const orderMenuDays = modelConstants?.orderMenuDays;
        const orderMenuSpecificDay = modelConstants?.orderMenuSpecificDay;
        const menuTime = orderMenuTimeFromModel(modelConstants);
        const generalTime = modelConstants?.orderTime ?? 22;

        // If they are different, show both set menu orders and regular orders
        const getSetMenuText = () => {
            const isSingleMenu = actualSetMenusCount === 1;
            const menuText = isSingleMenu
                ? orderStrings.doubleOrdersUntilWeekdayOneMenu
                : <>{orderStrings.doubleOrdersForSetMenusUntilPrefix} {actualSetMenusCount}{orderStrings.doubleStandardSetMenusUntil}</>;

            if (orderMenuSpecificDay >= 0) {
                return <>
                    {menuText}
                    <span style={{ fontWeight: 'bold' }}>
                        {getDayName(orderMenuSpecificDay)} {menuTime}:00
                    </span>
                    {orderStrings.doubleTheWeekBefore}
                </>;
            } else if (orderMenuDays === 0) {
                const menuTextDays = isSingleMenu
                    ? orderStrings.doubleOrdersUntilTimeOneMenu
                    : <>{orderStrings.doubleOrdersForSetMenusUntilPrefix} {actualSetMenusCount}{orderStrings.doubleStandardSetMenusUntilTime}</>;
                return <>
                    {menuTextDays}
                    <span style={{ fontWeight: 'bold' }}>{menuTime}:00</span>
                    {orderStrings.doubleTheSameDay}
                </>;
            } else if (orderMenuDays === 1) {
                const menuTextDay = isSingleMenu
                    ? orderStrings.doubleOrdersUntilTimeOneMenu
                    : <>{orderStrings.doubleOrdersForSetMenusUntilPrefix} {actualSetMenusCount}{orderStrings.doubleStandardSetMenusUntilTime}</>;
                return <>
                    {menuTextDay}
                    <span style={{ fontWeight: 'bold' }}>{menuTime}:00</span>
                    {orderStrings.doubleThePrevDay}
                </>;
            } else {
                const menuTextMultiple = isSingleMenu
                    ? orderStrings.doubleOrdersUntilTimeOneMenu
                    : <>{orderStrings.doubleOrdersForSetMenusUntilPrefix} {actualSetMenusCount}{orderStrings.doubleStandardSetMenusUntilTime}</>;
                return <>
                    {menuTextMultiple}
                    <span style={{ fontWeight: 'bold' }}>{menuTime}:00, {orderMenuDays}</span>
                    {orderStrings.doubleNDaysBefore}
                </>;
            }
        };

        const getRegularOrdersText = () => {
            // Don't show daily meals text if there are no daily meals available
            if (!dailyMealsList || dailyMealsList.length === 0) {
                return <>.</>;
            }

            if (orderDays === 0) {
                return <>
                    {orderStrings.doubleDailyMealsUntil}
                    <span style={{ fontWeight: 'bold' }}>{generalTime}:00</span>
                    {orderStrings.doubleDailySameDaySuffix}
                </>;
            } else if (orderDays === 1) {
                return <>
                    {orderStrings.doubleDailyMealsUntil}
                    <span style={{ fontWeight: 'bold' }}>{generalTime}:00</span>
                    {orderStrings.doubleDailyPrevDaySuffix}
                </>;
            } else {
                return <>
                    {orderStrings.doubleDailyMealsUntil}
                    <span style={{ fontWeight: 'bold' }}>{generalTime}:00, {orderDays}</span>
                    {orderStrings.doubleDailyNDaysSuffix}
                </>;
            }
        };

        return <>
            {getSetMenuText()}
            {getRegularOrdersText()}
        </>;
    };
    // Function to get double layout cancellations text — always general deadline (`orderTime` / `orderDays`), not set-menu weekday.
    const getDoubleLayoutCancellationsText = () => {
        const generalTime = modelConstants?.orderTime ?? 22;
        const orderDays = modelConstants?.orderDays;

        if (orderDays === 0) {
            return <>
                {orderStrings.doubleCancelUntilTimePrefix}
                <span style={{ fontWeight: 'bold' }}>{generalTime}:00</span>
                {orderStrings.doubleCancelSameDayNoRefund}
            </>;
        }
        if (orderDays === 1 || orderDays == null) {
            return <>
                {orderStrings.doubleCancelUntilTimePrefix}
                <span style={{ fontWeight: 'bold' }}>{generalTime}:00</span>
                {orderStrings.doubleCancelPrevDayNoRefund}
            </>;
        }
        return <>
            {orderStrings.doubleCancelUntilTimePrefix}
            <span style={{ fontWeight: 'bold' }}>{generalTime}:00, {orderDays}</span>
            {orderStrings.doubleCancelNDaysNoRefund}
        </>;
    };

    const shouldShowStickyBar = !isFormDisabled && (
        (isEditingOrder || (hasPackageInOrder && orderOtherThanPackage)) ||
        !!hasPlacedOrder ||
        isChangingOrder
    );

    const showClearOrCompleteActions =
        !isFormDisabled && !isChangingOrder && (isEditingOrder || (hasPackageInOrder && orderOtherThanPackage));
    // Single “view basket” (no clear-changes row): only when there is a staged draft to open the drawer for.
    // Empty days and package-only with no edits use the two-button sticky row when isEditingOrder, or no bar at all.
    const showCompleteOrderOnlyAction =
        !isFormDisabled && !isChangingOrder && !shouldHideCompleteOrderButton && !isEditingOrder && (!hasPackageInOrder || !orderOtherThanPackage) && orderValid;
    const showPlacedOrderActions = !!hasPlacedOrder && !isChangingOrder && !isPast && !draftDiffersFromPlaced;
    const showChangingOrderActions = isChangingOrder;
    const showOrderStickyActions =
        showClearOrCompleteActions || showCompleteOrderOnlyAction || showPlacedOrderActions || showChangingOrderActions;

    const showOrderCorePanel = !(
        (cannotOrder && !hasActivePlacedOrderForDay) ||
        (isWholeSchoolClosedDay && !hasPlacedPricedOrder && !hasPackageWithShowOrderOnClosed)
    );

    const showOrderAmountBlock = !(modelConstants?.packagesOnly && !modelConstants?.chargePackageExchangePrice);

    const orderAmountHasMeals = isEditingOrder || !!hasPlacedOrder || isChangingOrder || draftDiffersFromPlaced;
    const orderPanelCompact = isWideOrderLayout && !orderAmountHasMeals;
    const showWideOrderMealsScrollLayout = isWideOrderLayout && showOrderCorePanel && !orderPanelCompact;
    const measureWideMealsScrollport = showWideOrderMealsScrollLayout;
    const wideOrderMealsScrollport = measureWideMealsScrollport;

    useLayoutEffect(() => {
        const rootStyle = document.documentElement.style;
        const dayChrome = document.querySelector<HTMLElement>("#orderApp .order-day-chrome-sticky");
        if (!dayChrome) {
            rootStyle.removeProperty("--order-day-chrome-sticky-height");
            return;
        }
        const syncDayChromeHeight = (): void => {
            rootStyle.setProperty("--order-day-chrome-sticky-height", `${dayChrome.offsetHeight}px`);
        };
        syncDayChromeHeight();
        const chromeRo = new ResizeObserver(syncDayChromeHeight);
        chromeRo.observe(dayChrome);
        return () => {
            chromeRo.disconnect();
            rootStyle.removeProperty("--order-day-chrome-sticky-height");
        };
    }, [showCheckoutUrgency, lang, isReady]);

    useLayoutEffect(() => {
        if (!measureWideMealsScrollport || !isReady) {
            clearOrderMealsScrollportMaxHeight();
            return;
        }

        let syncFrame = 0;
        const scheduleSync = (force = false): void => {
            if (syncFrame) {
                cancelAnimationFrame(syncFrame);
            }
            syncFrame = requestAnimationFrame(() => {
                syncOrderMealsScrollLayoutCssVars({ force });
                syncFrame = 0;
            });
        };
        syncOrderMealsScrollLayoutCssVars({ force: true });

        const banner = document.querySelector('#orderApp .order-info-banner-section');
        const dayChrome = document.querySelector('#orderApp .order-day-chrome-sticky');
        const daySelector = document.querySelector('#orderApp .order-day-chrome-sticky .day-selector');
        const catalog = document.querySelector('#orderApp .order-scroll-sticky-region > .order-catalog-heading');
        const amount = document.querySelector('#orderApp .order-scroll-sticky-region .order-amount-block');
        const actions = document.querySelector('#orderApp .order-scroll-sticky-region .order-sticky-actions-bar');
        const ro = new ResizeObserver(() => scheduleSync(false));
        if (banner) {
            ro.observe(banner);
        }
        if (dayChrome) {
            ro.observe(dayChrome);
        }
        if (daySelector) {
            ro.observe(daySelector);
        }
        if (catalog) {
            ro.observe(catalog);
        }
        if (amount) {
            ro.observe(amount);
        }
        if (actions) {
            ro.observe(actions);
        }

        const onResize = (): void => scheduleSync(true);
        const viewport = window.visualViewport;
        viewport?.addEventListener('resize', onResize);
        window.addEventListener('resize', onResize);
        const unbindColumnCapSync = bindOrderMealsListCapSyncOnColumnScroll();

        return () => {
            ro.disconnect();
            unbindColumnCapSync();
            if (syncFrame) {
                cancelAnimationFrame(syncFrame);
            }
            viewport?.removeEventListener('resize', onResize);
            window.removeEventListener('resize', onResize);
            clearOrderMealsScrollportMaxHeight();
        };
    }, [measureWideMealsScrollport, isReady, date, showOrderStickyActions, showCheckoutUrgency]);

    useLayoutEffect(() => {
        if (!wideOrderMealsScrollport || !isReady) {
            return;
        }
        const mealsScrollEl = getOrderMealsListScrollEl();
        if (!mealsScrollEl || !isWideMealsListBodyScroll(mealsScrollEl)) {
            return;
        }
        return bindOrderMealsScrollChaining(mealsScrollEl);
    }, [wideOrderMealsScrollport, isReady, date, showOrderStickyActions]);

    useLayoutEffect(() => {
        if (!isWideOrderLayout) {
            return;
        }
        const daySelector = document.querySelector<HTMLElement>('.order-day-chrome-sticky .day-selector');
        if (!daySelector) {
            return;
        }
        return bindOrderDaySelectorStuckState(daySelector);
    }, [isWideOrderLayout, isReady, date]);

    // Early return after all hooks (including layout effects above) are defined
    if (orderComplete && !changingOrder) {
        const isOrderCompletion = orderCompleteIsCompletion ?? true;
        return <OrderComplete onReturn={() => { setOrderComplete(false); setOrderCompleteIsCompletion(null); }} isOrderCompletion={isOrderCompletion} date={date} />
    }

    const orderCatalogHeading = (
        <div className='order-catalog-heading'>
            <div className='order-headings'>
                {cannotOrder && orderStrings.headingCompletedOrder}
                {!cannotOrder && orderStrings.headingOrder}
            </div>
            <button type="button" className="order-week-print-button" onClick={() => reactToPrint()}>
                <PrintIcon className="order-week-print-button-icon" />
                <span>{orderStrings.printWeekOrders}</span>
            </button>
        </div>
    );

    const orderAmountBlock = showOrderAmountBlock && (
        <div className={`order-amount-block${orderAmountHasMeals ? ' order-amount-block--has-meals' : ' order-amount-block--empty'}`}>
            <div className='order-amount-lbl'>{orderStrings.orderAmountLabel}</div>
            <div className='order-amount-val'>{totalPrice != null ? formatPriceEuro(Number(totalPrice), lang) : '---'}</div>
        </div>
    );

    return (
        <div className={`base_margin_setup${!isWideOrderLayout && shouldShowStickyBar ? ' has-sticky-bar' : ''}`}>

            {loadingSpinner && <LoadingSpinner />}
            {dlgCancelOrder &&
                <YesNoDlg
                    title={commonStrings.cancelOrder}
                    msg={getSetMenuWarningMessage() ? `${getSetMenuWarningMessage()}\n\n${commonStrings.areYouSure}` : commonStrings.areYouSure}
                    yes={commonStrings.cancelation}
                    no={commonStrings.no}
                    onNo={() => setDlgCancelOrder(false)}
                    onYes={cancelorder}
                />
            }
            {orderActionErrorMsg !== null && (
                <OkDlg title={commonStrings.errorTitle} msg={orderActionErrorMsg} onClose={() => setOrderActionErrorMsg(null)} />
            )}

            {/* <fieldset disabled={false /* ordering || placedOrder && true || false }> */}
            {false && (
                <div className="menu_covid">
                    {orderStrings.covidMainCoursesNotice}
                    <br />
                    {orderStrings.restaurantClosedUntilRegulations}
                </div>
            )}
            <div
                className={isWideOrderLayout ? `order-scroll-sticky-region${showWideOrderMealsScrollLayout ? ' order-scroll-sticky-region--has-actions' : ''}${orderPanelCompact ? ' order-scroll-sticky-region--no-meals' : ''}${showWideOrderMealsScrollLayout && !showOrderStickyActions ? ' order-scroll-sticky-region--no-sticky-actions' : ''}` : undefined}
                style={isWideOrderLayout ? undefined : { display: 'contents' }}
            >
            {!modelConstants?.packagesOnly && (
                <OrderInfoBannerSection
                    doubleLayout={!!ordersTextDoubleLayout}
                    renderSingleLayoutText={getSingleLayoutText}
                    renderDoubleLayoutOrdersText={getDoubleLayoutOrdersText}
                    renderDoubleLayoutCancellationsText={getDoubleLayoutCancellationsText}
                />
            )}

            <div className="only-print" ref={contentRef} >
                <WeeklyOrders date={toDateFromServer(date)} />
            </div>

            <div className="order-day-chrome-sticky">
                {showCheckoutUrgency && (
                    <div className="order-checkout-urgency-wrap">
                        <CheckoutDeadlineUrgencyAlert
                            urgency={checkoutUrgency}
                            variant="compact"
                            modelConstants={basketModelConstants ?? modelConstants ?? null}
                            onOpenBasket={openBasket}
                            onDismiss={dismissCheckoutUrgency}
                        />
                    </div>
                )}
                <DaySelector date={date} cannotOrder={cannotOrder} onChange={onOrderDateChange} navigationDisabled={basketLocked} />
            </div>

            {isWideOrderLayout && showOrderCorePanel && orderCatalogHeading}

            <div
                className={isWideOrderLayout ? `order-body-scroll${orderPanelCompact ? ' order-body-scroll--compact' : ''}` : undefined}
                style={isWideOrderLayout ? undefined : { display: 'contents' }}
            >
            <div
                className={isWideOrderLayout ? `order-meals-scrollport${orderPanelCompact ? ' order-meals-scrollport--compact' : ''}` : undefined}
                style={isWideOrderLayout ? undefined : { display: 'contents' }}
            >
            <div id="order-day-content-start" className="order-day-content-start" />

            {isAnyClosureDay && hasPackageWithShowOrderOnClosed && hasOrderPeopleWithoutPackage && (
                <div className='closed-exchange-hint'>
                    {orderStrings.closedSchoolExchangeOnlyHint}
                </div>
            )}
            {isWholeSchoolClosedDay && disableChangeMyOrderSchoolClosed && (
                <div className='closed-cancel-only-hint'>
                    <img src="/img-assets/alert2.svg" className='closed-cancel-only-hint-icon' alt="" />
                    {orderStrings.closedSchoolCancelOnlyHint}
                </div>
            )}
            {isChangingOrder && isReady && (
                <div
                    className={`changing-order-callout${
                        changeOrderIsEmptyDraft
                            ? ' changing-order-callout--empty-blocked'
                            : changingOrderHasUnsubmittedChanges
                                ? ' changing-order-callout--staged'
                                : ' changing-order-callout--no-edits'
                    }`}
                    role="status"
                >
                    <div className="changing-order-callout__icon" aria-hidden>
                        {changeOrderIsEmptyDraft && <WarningAmberOutlinedIcon fontSize="small" />}
                        {!changeOrderIsEmptyDraft && changingOrderHasUnsubmittedChanges && <ShoppingBasketOutlinedIcon fontSize="small" />}
                        {!changeOrderIsEmptyDraft && !changingOrderHasUnsubmittedChanges && <EditOutlinedIcon fontSize="small" />}
                    </div>
                    <div className="changing-order-callout__text">
                        {changeOrderIsEmptyDraft ? (
                            <>
                                <p className="changing-order-callout__eyebrow">{uppercaseForLang(orderStrings.changingOrderHintEyebrow, lang)}</p>
                                <p className="changing-order-callout__title">{orderStrings.changingOrderHintTitle}</p>
                                <p className="changing-order-callout__detail">{orderStrings.changingOrderHintEmptyDetail}</p>
                            </>
                        ) : (
                            <>
                                <p className="changing-order-callout__eyebrow">{uppercaseForLang(orderStrings.changingOrderHintEyebrow, lang)}</p>
                                <p className="changing-order-callout__title">{orderStrings.changingOrderHintTitle}</p>
                                <p className="changing-order-callout__detail">
                                    {changingOrderHasUnsubmittedChanges
                                        ? orderStrings.changingOrderHintInBasketDetail
                                        : orderStrings.changingOrderHintNoEditsDetail}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}

            {(cannotOrder && !hasActivePlacedOrderForDay) &&
                (isEditingOrder &&
                    <div style={{ textAlign: 'center', margin: '100px 0 200px' }}>
                        <img src="/img-assets/empty-state-logo.svg" style={{ marginRight: '8.5px' }} />
                        <div className='empty-state-text'>
                            {orderStrings.incompleteOrderEmptyState}
                        </div>
                    </div>
                    ||
                    <div style={{ textAlign: 'center', margin: '100px 0 200px' }}>
                        <img src="/img-assets/empty-state-logo.svg" style={{ marginRight: '8.5px' }} />
                        <div className='empty-state-text'>
                            {orderStrings.noOrderEmptyState}
                        </div>
                    </div>
                )
                ||
                isWholeSchoolClosedDay && !hasPlacedPricedOrder && !hasPackageWithShowOrderOnClosed &&
                <div style={{ textAlign: 'center', margin: '100px 0 200px' }}>
                    <img src={`/img-assets/${schoolClosedIcon}`} style={{ marginRight: '8.5px' }} />
                    <div className='empty-state-text'>
                        {schoolClosedText}
                    </div>
                </div>
                ||
                <div className={`order-core ${cannotOrder ? 'past' : ''}${!orderAmountHasMeals ? ' order-core--no-meals' : ''}`}>
                    {!isWideOrderLayout && orderCatalogHeading}

                    {isReady
                        && <>
                            <div className="order-core-body" style={{ position: 'relative' }}>
                                <Modal enabled={!!hasPlacedOrder && !isChangingOrder && !draftDiffersFromPlaced} />
                                {
                                    choosingSnack &&
                                    // <OrderPopup close='OK' onClose={onSnackPopupClosed} >
                                    //     <SnacksPopUp date={date} snacksList={snacksList} typesList={snackTypes} recipient={choosingSnack.recipient}
                                    //         schoolBreak={choosingSnack.schoolBreak} snapshot={OrderSnacksLists} />
                                    // </OrderPopup>
                                    <TabsPopup tabSections={snackSectionTitles} close='OK' onClose={onSnackPopupClosed} >
                                        <SnacksPopUp date={date} snacksList={snacksList} typesList={snackTypes} recipient={choosingSnack.recipient}
                                            schoolBreak={choosingSnack.schoolBreak} snapshot={OrderSnacksLists} />
                                    </TabsPopup>
                                    ||
                                    choosingMeal &&
                                    (isSetDayMenu ?
                                        <TwoButtonPopup rightButtonText='OK' leftButtonText={commonStrings.clearOrder} isDisabled={false} onRightButtonClick={onMealPopupClosed} onLeftButtonClick={onSetDayMenuPopupClosed} >
                                            <SetDayMenuPopUp date={date} mealsList={mealsList} typesList={courseTypes} recipient={choosingMeal.recipient}
                                                schoolBreak={choosingMeal.schoolBreak} OrderMealsLists={OrderMealsLists} />
                                        </TwoButtonPopup>
                                        :
                                        <OrderPopup close='OK' onClose={onMealPopupClosed} >
                                            <MealsPopUp date={date} menusSnapshot={menusSnapshot} dailyMealsList={dailyMealsList} typesList={courseTypes} recipient={choosingMeal.recipient}
                                                schoolBreak={choosingMeal.schoolBreak} OrderMealsLists={OrderMealsLists} actualSetMenusCount={actualSetMenusCount} />
                                        </OrderPopup>
                                    )
                                    ||
                                    <>
                                        {
                                            filteredStudentsList.map(sPerson => {
                                                const person = sPerson.val() as OrderPerson;
                                                const isStudent = !!person?.student;
                                                const personOrders = WholeOrderLists.filter(orderList => orderList.child('recipient').val() === sPerson.key);
                                                const sPackageOrder: DataSnapshot<MealLine> = personOrders.filter(co => !!co.child('isPackage').val())?.[0];// only one package allowed
                                                const schoolBreaks: string[] = Object.keys(personOrders.reduce((prev, orderLine) => {
                                                    prev[orderLine.child('schoolBreak').val()] = 1;
                                                    return prev;
                                                }, {} as { [key: string]: 1 }));

                                                const personName = `${person.firstName} ${person.lastName}`;
                                                const personTitle = isStudent ?
                                                    (() => {
                                                        const student = person as Student;
                                                        const classroomKey = student?.classroom;
                                                        if (!classroomKey) return personName;

                                                        // Get the classroom name in current language
                                                        const classroomData = classroomsList?.find(c => c.key === classroomKey);
                                                        const classroomName = classroomData?.val()?.name?.[lang] || classroomKey;

                                                        return `${personName} (${classroomName})`;
                                                    })() :
                                                    <>
                                                        {personName} ({orderStrings.staffLabel})
                                                    </>;

                                                const student = isStudent ? person as Student : null;
                                                const classroomOnlyClosure = student ? getClassroomOnlyClosureForStudent(student.classroom) : null;
                                                const recipientIsClosed = isWholeSchoolClosedDay || !!classroomOnlyClosure;
                                                const recipientHasPackageException = !!student && recipientIsClosed && userPackagesList?.some(userPackage => {
                                                    if (userPackage.child('recipient').val() !== sPerson.key) return false;
                                                    const packageId = userPackage.val().packageId;
                                                    if (!packageId || sMealPackages?.child(packageId).child('showOrderOnSchoolClosed').val() !== true) {
                                                        return false;
                                                    }
                                                    const start = userPackage.child('start').val();
                                                    const end = userPackage.child('end').val();
                                                    if (!start || !end) return false;
                                                    return toDateFromServer(date) >= toDateFromServer(start) && toDateFromServer(date) <= toDateFromServer(end);
                                                });
                                                const recipientOrderDisabled = isFormDisabled || (recipientIsClosed && !recipientHasPackageException);
                                                const recipientLineDisabled = isFormDisabled || ((recipientIsClosed && !recipientHasPackageException) && !isChangingOrder);
                                                const recipientHasPlacedNormalContent = recipientsWithPlacedNormalContent.has(sPerson.key);
                                                const showRecipientClosedIntro = !!classroomOnlyClosure && !recipientHasPackageException;
                                                const showRecipientClosedAction = showRecipientClosedIntro && recipientHasPlacedNormalContent;
                                                const showRecipientClosedOrderLines = showRecipientClosedAction;

                                                return (
                                                    <div key={sPerson.key} className={`rectangle-gray${showRecipientClosedIntro ? ' rectangle-gray--recipient-closed' : ''}`}>
                                                        <div className='header-child'>{personTitle}</div>
                                                        {showRecipientClosedIntro && classroomOnlyClosure?.closedText &&
                                                            <div className="recipient-closed-hint">
                                                                <span className="recipient-closed-hint__icon">
                                                                    <ClosureAttentionIcon iconOnly />
                                                                </span>
                                                                <span className="recipient-closed-hint__text">
                                                                    <span className="recipient-closed-hint__line">{orderStrings.recipientClassroomClosedIntro(classroomOnlyClosure.closedText)}</span>
                                                                    {showRecipientClosedAction &&
                                                                        <span className="recipient-closed-hint__line">{orderStrings.recipientClassroomClosedAction(orderStrings.changeMyOrder)}</span>
                                                                    }
                                                                </span>
                                                            </div>
                                                        }
                                                        {!!sPackageOrder && sMeals && (!recipientIsClosed || recipientHasPackageException) &&
                                                            <Package key={sPackageOrder.key} date={date} sFoods={sMeals} mealsList={mealsList} dailyMealsList={dailyMealsList} userPackage={userPackagesList?.find(usl => usl.child('recipient').val() === sPerson.key)?.val()} sMealLine={sPackageOrder} courseTypes={courseTypes} isFormDisabled={recipientOrderDisabled || disableWholeFormSchoolClosedPackageDay} modelConstants={modelConstants} sideDish={sideDish} dessert={dessert} discountPercentage={discountPercentage} menuData={menuData} shouldHideExchangeButton={shouldHideExchangeButton} />
                                                        }
                                                        {modelConstants?.packagesOnly ? (
                                                            (null)
                                                        ) : showRecipientClosedOrderLines ? (
                                                            <>
                                                                {schoolBreaks.map(schoolBreak => <React.Fragment key={schoolBreak}>
                                                                    <div className="order-food-list">
                                                                        {personOrders
                                                                            .filter(orderLine => (orderLine.child('schoolBreak').val() === schoolBreak) && !orderLine.child('isPackage').val())
                                                                            .map(orderBreak => {
                                                                                const isSnackLine = orderBreak?.child('snackKey').exists();
                                                                                const mealKey = orderBreak?.child('key')?.val();
                                                                                const isMenuMeal = !isSnackLine && mealsList?.some(m => m.key === mealKey);
                                                                                const dataset = isSnackLine ? sSnacks : (isMenuMeal ? sMeals : sDailyMeals);
                                                                                const mainMenuWasOnPlacedOrder = draftMainMenuLineWasOnPlacedOrder(orderBreak, isChangingOrder, placedOrderMealsList);
                                                                                return (
                                                                                    <OrderLine
                                                                                        key={orderBreak.key}
                                                                                        date={date}
                                                                                        snapshot={orderBreak}
                                                                                        sFoods={dataset}
                                                                                        dailyMealsList={dailyMealsList}
                                                                                        isFormDisabled={recipientLineDisabled}
                                                                                        pricesExtra={pricesExtra}
                                                                                        sideDish={sideDish}
                                                                                        dessert={dessert}
                                                                                        isSetDayMenu={isSetDayMenu}
                                                                                        discountPercentage={discountPercentage}
                                                                                        mainMenuWasOnPlacedOrder={mainMenuWasOnPlacedOrder}
                                                                                    />
                                                                                );
                                                                            })}
                                                                    </div>
                                                                </React.Fragment>)}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {schoolBreaks.map(schoolBreak => <React.Fragment key={schoolBreak}>
                                                                    <OrderBlock OrderSnacksLists={OrderSnacksLists} OrderMealsLists={OrderMealsLists} recipient={sPerson.key} schoolBreak={schoolBreak} sSchoolBreaks={sSchoolBreaks} setChoosingSnack={setChoosingSnack} setChoosingMeal={setChoosingMeal} isFormDisabled={recipientOrderDisabled} modelConstants={modelConstants} />
                                                                    <div className="order-food-list">
                                                                        {personOrders
                                                                            .filter(orderLine => (orderLine.child('schoolBreak').val() === schoolBreak) && !orderLine.child('isPackage').val())
                                                                            .map(orderBreak => {
                                                                                const isSnackLine = orderBreak?.child('snackKey').exists();
                                                                                const mealKey = orderBreak?.child('key')?.val();
                                                                                const isMenuMeal = !isSnackLine && mealsList?.some(m => m.key === mealKey);
                                                                                const dataset = isSnackLine ? sSnacks : (isMenuMeal ? sMeals : sDailyMeals);
                                                                                const mainMenuWasOnPlacedOrder = draftMainMenuLineWasOnPlacedOrder(orderBreak, isChangingOrder, placedOrderMealsList);
                                                                                return (
                                                                                    <OrderLine
                                                                                        key={orderBreak.key}
                                                                                        date={date}
                                                                                        snapshot={orderBreak}
                                                                                        sFoods={dataset}
                                                                                        dailyMealsList={dailyMealsList}
                                                                                        isFormDisabled={recipientLineDisabled}
                                                                                        pricesExtra={pricesExtra}
                                                                                        sideDish={sideDish}
                                                                                        dessert={dessert}
                                                                                        isSetDayMenu={isSetDayMenu}
                                                                                        discountPercentage={discountPercentage}
                                                                                        mainMenuWasOnPlacedOrder={mainMenuWasOnPlacedOrder}
                                                                                    />
                                                                                );
                                                                            })}
                                                                    </div>
                                                                </React.Fragment>)}
                                                                {(!hasActiveOrder(sPerson.key) || addingOrder === sPerson.key) &&
                                                                    <OrderBlock
                                                                        OrderSnacksLists={OrderSnacksLists}
                                                                        OrderMealsLists={OrderMealsLists}
                                                                        recipient={sPerson.key}
                                                                        schoolBreak={null}
                                                                        sSchoolBreaks={sSchoolBreaks}
                                                                        setChoosingSnack={setChoosingSnack}
                                                                        setChoosingMeal={setChoosingMeal}
                                                                        isFormDisabled={recipientOrderDisabled}
                                                                        modelConstants={modelConstants}
                                                                    />
                                                                }
                                                                {!recipientOrderDisabled && (hasActiveOrder(sPerson.key) && addingOrder != sPerson.key && hasEmptyBreak(sPerson.key)) &&
                                                                    <button type="button" onClick={() => { onAddingOrder(sPerson.key) }} className='order-add-item'>
                                                                        {orderStrings.addOrderAnotherBreak}
                                                                    </button>}
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            )
                                        }
                                    </>
                                }
                                {modelConstants?.allowCommentsPerOrder && (!cannotOrder || orderRemarks) &&
                                    <>
                                        <div id="order-remarks-heading" className='order-headings' style={{ marginTop: 40 }}>
                                            {orderStrings.remarksHeading}
                                        </div>
                                        <div className='order-remarks-box'>
                                            <textarea id="order-remarks-field" aria-labelledby="order-remarks-heading" placeholder={orderRemarksPlaceholder} value={orderRemarks ?? ""} onChange={e => setOrderRemarks(e.target.value)} />
                                        </div>
                                    </>
                                }
                                {!isWideOrderLayout && orderAmountBlock}
                                {recipientMissing && <div className='order-error-msg'>
                                    {orderStrings.fillSnackRecipient}
                                </div>}

                            </div>
                            {!isWideOrderLayout && (
                                <>
                            {/* Show Clear Changes and Complete Order buttons when there are changes to any order */}
                            {!isFormDisabled && !isChangingOrder && (isEditingOrder || (hasPackageInOrder && orderOtherThanPackage)) &&
                                <div>
                                    <div className={`two-buttons-flex ${shouldShowStickyBar ? 'sticky-bottom-bar' : ''}`}>

                                        <button
                                            className={'hollow-button'}
                                            onClick={cancelChangeOrder}
                                            style={{
                                                position: 'relative',
                                                zIndex: 10
                                            }}
                                        >
                                            {orderStrings.clearChanges}
                                        </button>

                                        <button
                                            className={`order-completed-button2 change-order-button ${!orderValid && 'disabled' || ''}`}
                                            disabled={!orderValid}
                                            onClick={orderValid && onSubmitOrder || null}
                                            style={{
                                                position: 'relative',
                                                zIndex: 10
                                            }}
                                        >
                                            {submitOrderLabel}
                                        </button>
                                    </div>
                                </div>
                            }

                            {/* Show only Complete Order button when there are no changes */}
                            {showCompleteOrderOnlyAction &&
                                <button className={`order-completed-button2 ${shouldShowStickyBar ? 'sticky-bottom-bar' : ''}`} onClick={onSubmitOrder}>
                                    {submitOrderLabel}
                                </button>
                            }

                            {!!hasPlacedOrder && !isChangingOrder && !isPast &&
                                <div >
                                    <div className={'two-buttons-flex sticky-bottom-bar'}>

                                        <button
                                            className={'hollow-button'}
                                            disabled={cannotCancelOrder}
                                            onClick={() => setDlgCancelOrder(true)}
                                            style={{
                                                position: 'relative',
                                                zIndex: 10
                                            }}
                                        >
                                            {orderStrings.cancelMyOrder}
                                        </button>

                                        <button
                                            className={'order-completed-button2 change-order-button'}
                                            disabled={cannotCancelOrder || disableChangeMyOrderSchoolClosed}
                                            onClick={() => { void changeOrder(); }}
                                            style={{
                                                position: 'relative',
                                                zIndex: 10
                                            }}
                                        >
                                            {orderStrings.changeMyOrder}
                                        </button>
                                    </div>
                                </div>
                            }

                            {isChangingOrder && <div >
                                <div className={`two-buttons-flex ${shouldShowStickyBar ? 'sticky-bottom-bar' : ''}`}>

                                    <button className={'hollow-button'} onClick={cancelChangeOrder}>
                                        {orderStrings.clearChanges}
                                    </button>

                                    <button className={`order-completed-button2 change-order-button ${!orderValid && 'disabled' || ''}`} disabled={!orderValid} onClick={orderValid && onSubmitOrder || null}>
                                        {sendChangesLabel}
                                    </button>
                                </div>
                            </div>}

                                </>
                            )}
                        </>
                        || <>{commonStrings.loading}</>}
                </div>
            }
            </div>
            </div>
            {isWideOrderLayout && showOrderCorePanel && isReady && orderAmountBlock}
            {isWideOrderLayout && showOrderStickyActions && (
                <div className="order-sticky-actions-bar">
                    {showClearOrCompleteActions && (
                        <div className="two-buttons-flex">
                            <button
                                className={'hollow-button'}
                                onClick={cancelChangeOrder}
                                style={{
                                    position: 'relative',
                                    zIndex: 10
                                }}
                            >
                                {orderStrings.clearChanges}
                            </button>
                            <button
                                className={`order-completed-button2 change-order-button ${!orderValid && 'disabled' || ''}`}
                                disabled={!orderValid}
                                onClick={orderValid && onSubmitOrder || null}
                                style={{
                                    position: 'relative',
                                    zIndex: 10
                                }}
                            >
                                {submitOrderLabel}
                            </button>
                        </div>
                    )}
                    {showCompleteOrderOnlyAction && (
                        <button className="order-completed-button2" onClick={onSubmitOrder}>
                            {submitOrderLabel}
                        </button>
                    )}
                    {showPlacedOrderActions && (
                        <div className="two-buttons-flex">
                            <button
                                className={'hollow-button'}
                                disabled={cannotCancelOrder}
                                onClick={() => setDlgCancelOrder(true)}
                                style={{
                                    position: 'relative',
                                    zIndex: 10
                                }}
                            >
                                {orderStrings.cancelMyOrder}
                            </button>
                            <button
                                className={'order-completed-button2 change-order-button'}
                                disabled={cannotCancelOrder || disableChangeMyOrderSchoolClosed}
                                onClick={() => { void changeOrder(); }}
                                style={{
                                    position: 'relative',
                                    zIndex: 10
                                }}
                            >
                                {orderStrings.changeMyOrder}
                            </button>
                        </div>
                    )}
                    {showChangingOrderActions && (
                        <div className="two-buttons-flex">
                            <button className={'hollow-button'} onClick={cancelChangeOrder}>
                                {orderStrings.clearChanges}
                            </button>
                            <button className={`order-completed-button2 change-order-button ${!orderValid && 'disabled' || ''}`} disabled={!orderValid} onClick={orderValid && onSubmitOrder || null}>
                                {sendChangesLabel}
                            </button>
                        </div>
                    )}
                </div>
            )}
            </div>
        </div >
    );
}

type OrderProps = {
    setMenu: (menu: string, shouldScrollToTop?: boolean) => void;
}

function Package({ date, sFoods, mealsList, dailyMealsList, userPackage, sMealLine, courseTypes, isFormDisabled, modelConstants, sideDish, dessert, discountPercentage, menuData, shouldHideExchangeButton }: PackageProps) {

    if (!sFoods || !mealsList || !sMealLine || !courseTypes || !sMealLine.key || !date)
        return <></>

    const [lang] = useContext(langContext);
    const orderStrings = useOrderStrings();
    const { uid } = auth.currentUser;
    const mealLine: MealLine = sMealLine?.val();

    // Check if recipient is a staff member by looking up in students list
    const orderPeople = useDatabase(studentsPath(uid));
    const recipientData = useDatabase(orderPeople ? `${studentsPath(uid)}/${mealLine.recipient}` : null);
    const recipientPerson = recipientData?.val() as OrderPerson;
    const recipientIsStaff = !!recipientPerson?.personnel;
    const recipientIsStudent = !recipientIsStaff;

    const meal: Meal = useMemo(() => {
        if (!mealLine)
            return null;
        return getMealData(mealLine.key, sFoods, dailyMealsList, menuData);
    }, [sFoods, mealLine, dailyMealsList, menuData]);

    const foodName = useMemo(() => {
        if (!mealLine)
            return null;
        return getMealName(mealLine.key, sFoods, dailyMealsList, lang, menuData);
    }, [sFoods, mealLine, dailyMealsList, lang, menuData]);

    const isSetMenu = meal?.courseType === 'main';
    const hasMenuExtras = (isSetMenu || meal?.isExchangable) && (mealLine?.quantitySide || mealLine?.quantityDessert);
    const staffDiscount: number = useDatabase(DISCOUNT_STAFF_PATH)?.val() || 0;
    const menu1: Meal = useDatabase(`${menuPath(currentMenu)}/${date}/menu1`)?.val();
    const menu1Price = recipientIsStudent ? menu1?.priceSmall :
        (!recipientIsStaff || (staffDiscount <= 0 && !menu1?.priceComboStaff)) ? applyDiscountToPrice(menu1?.price, discountPercentage) :
            getPersonnelPrice(menu1?.price, staffDiscount);

    const hasOrderItemRemark = !!mealLine?.orderItemRemark;
    const [orderItemRemarkPopup, setOrderItemRemarkPopup] = useState(false);
    const onOrderItemRemarkPressed = () => {
        setOrderItemRemarkPopup(true);
    };

    const [changeMenuPopUp, setChangeMenuPopUp] = useState(false);

    const packageDescription = getLangValue(meal?.description, lang);
    const exchangePrice = modelConstants?.chargePackageExchangePrice && meal?.menuType != 1
        ? getPackageExchangeMealPrice(recipientIsStudent, recipientIsStaff, menu1Price, meal, userPackage?.discount ?? 0, staffDiscount, mealLine?.quantity, modelConstants?.chargePackageExchangePrice)
        : null;
    const showExchangePrice = exchangePrice != null && Number(exchangePrice) > 0;

    return <div>
        {orderItemRemarkPopup &&
            <OrderPopup close='OK' onClose={() => setOrderItemRemarkPopup(false)} >
                <OrderItemRemarkPopUp snapshot={sMealLine} />
            </OrderPopup>
        }
        {changeMenuPopUp &&
            <OrderPopup close='OK' onClose={() => setChangeMenuPopUp(false)} >
                <PackageMealsPopUp date={date} mealsList={mealsList} dailyMealsList={dailyMealsList} typesList={courseTypes} sMealLine={sMealLine} discount={userPackage?.discount ?? 0} />
            </OrderPopup>

        }
        <div className='order-package-card'>
            <div className='order-package-card__label'>
                <img className='order-package-card__icon' src="/img-assets/packages.svg" alt="" />
                <span className='order-package-card__package-title'>{userPackage?.title?.[lang]}</span>
            </div>
            <div className='order-package-card__content'>
                <div className='order-package-card__meal-block'>
                    <div className={`order-package-card__meal ${isFormDisabled ? 'disabled' : ''}`}>
                        <div className='order-package-card__meal-title'>{foodName}</div>
                        {!!packageDescription && <div className='order-package-card__meal-description'>{packageDescription}</div>}
                        {hasMenuExtras &&
                            <MenuExtraChecklist meal={meal} date={date} quantitySide={mealLine?.quantitySide} quantityDessert={mealLine?.quantityDessert} sideDish={sideDish} dessert={dessert} variant="inline" />
                        }
                    </div>
                    <div className='order-package-card__actions'>
                        {modelConstants?.allowCommentsPerFood &&
                            <button type='button' className='order-package-card__remark' disabled={isFormDisabled} onClick={onOrderItemRemarkPressed} aria-label={orderStrings.orderItemRemarkAria}>
                                <img src={hasOrderItemRemark ? "/img-assets/order-item-remark.svg" : "/img-assets/no-order-item-remark.svg"} alt="" />
                            </button>
                        }
                        {!shouldHideExchangeButton &&
                            <button type='button' className='order-package-card__change' disabled={isFormDisabled} onClick={() => setChangeMenuPopUp(true)}>
                                <span>{orderStrings.packageChangeMeal}</span>
                                <ReplayIcon className='order-package-card__change-icon' />
                            </button>
                        }
                    </div>
                </div>
                {showExchangePrice &&
                    <div className={`order-package-card__price ${isFormDisabled ? 'disabled' : ''}`}>
                        {formatPriceEuro(Number(exchangePrice), lang)}
                    </div>
                }
            </div>
        </div>
    </div >
}

type PackageProps = {
    date: string
    sFoods: DataSnapshot<Lookup<BaseFood>>
    mealsList: DataSnapshot[]
    dailyMealsList: DataSnapshot<Meal>[]
    userPackage: UserMealPackage
    sMealLine: DataSnapshot<MealLine>
    courseTypes: DataSnapshot[]
    isFormDisabled: boolean
    modelConstants: ModelComplexity
    sideDish: Meal
    dessert: Meal
    discountPercentage: number
    menuData: { [key: string]: Meal }
    shouldHideExchangeButton: boolean
}


function OrderBlock({ OrderSnacksLists, OrderMealsLists, recipient, schoolBreak, sSchoolBreaks, setChoosingSnack, setChoosingMeal, isFormDisabled, modelConstants }: OrderBlockProps) {

    const [lang] = useContext(langContext);
    const orderStrings = useOrderStrings();

    const hasSelectedSnack = OrderSnacksLists?.filter(orderPerBreak => orderPerBreak.child('schoolBreak').val() === schoolBreak && orderPerBreak.child('recipient').val() === recipient)?.length > 0;
    const OrderMealsListsBreak = OrderMealsLists?.filter(orderPerBreak => orderPerBreak.child('schoolBreak').val() === schoolBreak && orderPerBreak.child('recipient').val() === recipient);
    const hasSelectedMeal = OrderMealsListsBreak?.length > 0;
    // const hasPackageInOrder = hasSelectedMeal && OrderMealsListsBreak.some(oml => oml.child('isPackage').val());
    const isOnlyPackageMeal = OrderMealsListsBreak?.length === 1 && OrderMealsListsBreak?.[0]?.child('isPackage')?.val();

    const [sBreak, setSBreak] = useState(schoolBreak);
    const onSchoolBreakChanged = (value: string) => {
        setSBreak(value);
        // setChoosingSnack({ recipient, schoolBreak: value });
    }

    const onChooseSnack = () => {
        setChoosingSnack({ recipient, schoolBreak: sBreak });
    }
    const onChooseMeal = () => {
        setChoosingMeal({ recipient, schoolBreak: sBreak });
    }

    const aSchoolBreaks = useList(sSchoolBreaks);
    const firstBreak = aSchoolBreaks?.[0]?.key;
    const hasOneBreak = aSchoolBreaks?.length === 1;
    const mealDisabled = !hasOneBreak && (!sBreak || sBreak === firstBreak);

    useEffect(() => {   // set starting quantity
        hasOneBreak && firstBreak && setSBreak(firstBreak);
    }, [hasOneBreak, sBreak, firstBreak]);

    return <div className='order-block-row'>
        <div className="order-delivery-field">
            <SchoolBreakDropdown OrderSnacksLists={OrderSnacksLists} OrderMealsLists={OrderMealsLists} sSchoolBreaks={sSchoolBreaks} recipient={recipient} selected={sBreak} selectedBreak={sSchoolBreaks.child(sBreak)} onChange={onSchoolBreakChanged} isFormDisabled={isFormDisabled} />
        </div>
        <div className="order-category-buttons">
            {modelConstants?.allowSnacks &&
                <Button className='order-category-btn' startIcon={<AddCircleOutlineIcon />} onClick={onChooseSnack} disabled={isFormDisabled || !sBreak}>{orderStrings.snackButtonLabel}</Button>
            }
            {(modelConstants?.allowMeals || getLangValue(modelConstants?.comingSoonMeals, lang).length > 0) &&
                <Button className='order-category-btn' startIcon={<AddCircleOutlineIcon />} onClick={onChooseMeal} disabled={isFormDisabled || mealDisabled || !modelConstants?.allowMeals}>
                    {!modelConstants?.allowMeals && <span>{modelConstants?.comingSoonMeals?.[lang]}</span>}
                    <span>{hasSelectedMeal ? isOnlyPackageMeal ? orderStrings.addExtraMeal : orderStrings.mealButtonLabel : orderStrings.mealButtonLabel}</span>
                </Button>
            }
        </div>
    </div>
}

type OrderBlockProps = {
    OrderSnacksLists: DataSnapshot[]
    OrderMealsLists: DataSnapshot[]
    recipient: string;
    schoolBreak: string;
    sSchoolBreaks: DataSnapshot;
    setChoosingSnack: (props: { recipient: string, schoolBreak: string }) => void
    setChoosingMeal: (props: { recipient: string, schoolBreak: string }) => void
    isFormDisabled: boolean
    modelConstants: ModelComplexity
}

export function MenuExtraChecklist({ meal, date, quantitySide, quantityDessert, sideDish, dessert, variant = 'checklist' }: MenuExtraStrProps) {

    const [lang] = useContext(langContext);
    const isSetMenu = meal?.courseType === 'main';
    const hasMenuExtras = (isSetMenu || meal?.isExchangable) && (quantitySide || quantityDessert);

    const dayNum = toDateFromServer(date).getDay();

    const mealSide = getLangValue(meal?.sideDish, lang);
    const sideDay: string = useDatabase(`${SIDE_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const sideMenu: string = !sideDish ? '' : getLangValue(sideDish.name, lang);
    const sideName: string = !mealSide ? !sideMenu ? sideDay : sideMenu : mealSide;
    const hasSide = !!quantitySide && !!sideName;

    const dessertDay: string = useDatabase(`${DESSERT_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const dessertMenu: string = !dessert ? '' : getLangValue(dessert.name, lang);
    const dessertName: string = !dessertMenu ? dessertDay : dessertMenu;
    const hasDessert = !!quantityDessert && !!dessertName;

    if (!hasMenuExtras) {
        return null;
    }

    if (variant === 'inline' || variant === 'inlineBasket') {
        const parts = [hasSide ? sideName : null, hasDessert ? dessertName : null].filter((part): part is string => !!part);
        if (parts.length === 0) {
            return null;
        }
        if (variant === 'inlineBasket') {
            return <div className='basket-line-extras'>{parts.join(', ')}</div>;
        }
        return <div className='order-package-card__meal-extras'>+ {parts.join(', ')}</div>;
    }

    return <div className='order-food-item__extras'>
        {hasSide &&
            <div className='order-food-item__extra-row'>
                <Checkbox size="small" checked disabled />
                <span>{sideName}</span>
            </div>
        }
        {hasDessert &&
            <div className='order-food-item__extra-row'>
                <Checkbox size="small" checked disabled />
                <span>{dessertName}</span>
            </div>
        }
    </div>;
}

type MenuExtraStrProps = {
    meal: Meal
    date: string
    quantitySide: number
    quantityDessert: number
    sideDish: Meal
    dessert: Meal
    /** Package card: one line "+ side, dessert" instead of checkboxes; inlineBasket: basket drawer extras line */
    variant?: 'checklist' | 'inline' | 'inlineBasket'
}

/** Set-menu line on the placed order for this recipient/break — used for post-deadline delete/qty warnings while changing order. */
function draftMainMenuLineWasOnPlacedOrder(
    orderBreak: DataSnapshot,
    isChangingOrder: boolean,
    placedOrderMealsList: DataSnapshot<MealLine>[] | undefined,
): boolean {
    if (!isChangingOrder || !placedOrderMealsList?.length) {
        return false;
    }
    if (orderBreak.child('snackKey').exists()) {
        return false;
    }
    const mealKey = orderBreak.child('key')?.val();
    if (!mealKey) {
        return false;
    }
    const recipient = orderBreak.child('recipient')?.val();
    const schoolBreak = orderBreak.child('schoolBreak')?.val();
    return placedOrderMealsList.some(pm => {
        const line = pm.val();
        return line?.key === mealKey && line?.recipient === recipient && line?.schoolBreak === schoolBreak;
    });
}

function OrderLine({ date, snapshot, sFoods, dailyMealsList, isFormDisabled, pricesExtra, sideDish, dessert, isSetDayMenu, discountPercentage, mainMenuWasOnPlacedOrder }: OrderLineProps) {

    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const deadlineAt = useOrderDeadlineAt();
    const { uid } = auth.currentUser;
    const recipient = snapshot?.child('recipient')?.val();

    // Check if recipient is a staff member by looking up in students list
    const students = useDatabase(studentsPath(uid));
    const recipientData = useDatabase(students ? `${studentsPath(uid)}/${recipient}` : null);
    const recipientPerson = recipientData?.val() as OrderPerson;

    const [lang] = useContext(langContext);
    const sFoodsList = useList(sFoods);
    const isSnack = !!sFoods ? (sFoodsList || [])[0]?.child('snackType')?.exists() : false;

    const foodLine: BaseOrderLine = snapshot.val();

    const { small: portionLabelSmall, medium: portionLabelMedium, large: portionLabelLarge } = usePortionSizeLabels();
    const hasPortionSize = !!foodLine.portionSize;
    const portionSizeStr = hasPortionSize ? foodLine.portionSize === PORTION_SIZE_SMALL ? portionLabelSmall : foodLine.portionSize === PORTION_SIZE_MEDIUM ? portionLabelMedium : portionLabelLarge : null;

    const food: string = isSnack ? snapshot?.child('snackKey')?.val() : snapshot?.child('key')?.val();
    const [quantity, setQuantity] = useSnapshotState<number>(snapshot, 'quantity');
    const [quantitySide, setQuantitySide] = useSnapshotState<number>(snapshot, 'quantitySide');
    const [quantityDessert, setQuantityDessert] = useSnapshotState<number>(snapshot, 'quantityDessert');

    const hasOrderItemRemark = snapshot?.child('orderItemRemark')?.val();
    const [orderItemRemarkPopup, setOrderItemRemarkPopup] = useState(false);
    const onOrderItemRemarkPressed = () => {
        setOrderItemRemarkPopup(true);
    };

    // Get menu data for the date to use correct pricing for menu items
    const menuSnapshot = useDatabase(`${menuPath(currentMenu)}/${date}`);
    const menuItems = useList(menuSnapshot);

    // Use the same logic as getMealData function for consistency
    const foodItem: Meal = useMemo(() => {
        if (!food || !sFoods)
            return null;

        if (!isSnack) {
            // For meals (not snacks):
            // 1. First try to find the meal in menu data (for package exchanges)
            for (const menuItem of menuItems || []) {
                if (menuItem.child('key').val() === food) {
                    // This is a menu item, use the menu data (which includes the correct pricing)
                    return menuItem.val();
                }
            }

            // 2. If not found in menu data, look in daily meals
            const dailyMeal = dailyMealsList?.find(dm => dm.key === food)?.val();
            if (dailyMeal) {
                return dailyMeal;
            }

            // 3. If not found in daily meals, look in traditional meals as fallback
            const traditionalMeal = sFoods?.child(food)?.val();
            return traditionalMeal || null;
        } else {
            // For snacks, only look in sFoods (which should be sSnacks)
            return sFoods?.child(food)?.val() || null;
        }
    }, [sFoods, food, menuItems, dailyMealsList, isSnack]);

    // Don't log warning if sFoods is still loading (undefined)
    if (!foodItem && sFoods !== undefined) {
        console.warn(`Food item data not found for key: ${food}`);
    }

    const isSetMenu = foodItem?.courseType === 'main';
    const staffDiscount: number = useDatabase(DISCOUNT_STAFF_PATH)?.val() || 0;
    const hasMenuExtras = isSetMenu && (quantitySide || quantityDessert);
    // Combo logic
    const combosData = useCombosData();

    // Check if we should show portion size (hide only if there's only large portion available)
    const shouldShowPortionSizeValue = shouldShowPortionSize(foodItem, combosData);

    const price = useMemo(() => {
        if (!food || !quantity) {
            return null;
        }

        // Create recipients object
        const recipients: { [key: string]: { personnel?: boolean, student?: boolean } } = {};
        if (recipient && recipientPerson) {
            recipients[recipient] = {
                personnel: recipientPerson.personnel === true,
                student: recipientPerson.student === true
            };
        }

        // Handle snacks separately from meals
        if (isSnack) {
            if (!sFoods) {
                return null;
            }

            // Convert sFoods (DataSnapshot) to SnacksList (object with snack keys)
            const snacks: SnacksList = {};
            sFoods.forEach((snackSnapshot) => {
                const snackKey = snackSnapshot.key;
                if (snackKey) {
                    snacks[snackKey] = snackSnapshot.val() as Snack;
                }
            });

            // Create SnackLine object from current state
            const snackLine: SnackLine = {
                snackKey: food,
                quantity,
                recipient: recipient || '',
                schoolBreak: foodLine.schoolBreak || '',
                orderItemRemark: foodLine.orderItemRemark
            };

            // Use the shared calculation function for snacks
            return calculateSnackLinePrice(
                snackLine,
                snacks,
                recipients,
                staffDiscount,
                discountPercentage
            );
        }

        // Handle meals
        if (!foodItem) {
            // If foodItem is null, this means the meal data is missing or corrupted
            // We should not allow ordering items with missing data
            return null;
        }

        // Convert data structures to match calculateMealLinePrice requirements
        // Convert sFoods (DataSnapshot) to MealsList (object with meal keys)
        const meals: MealsList = {};
        if (sFoods) {
            sFoods.forEach((mealSnapshot) => {
                const mealKey = mealSnapshot.key;
                if (mealKey) {
                    meals[mealKey] = mealSnapshot.val() as Meal;
                }
            });
        }

        // Convert dailyMealsList (DataSnapshot[]) to MealsList
        const dailyMeals: MealsList = {};
        dailyMealsList?.forEach((mealSnapshot) => {
            const mealKey = mealSnapshot.key;
            if (mealKey) {
                dailyMeals[mealKey] = mealSnapshot.val();
            }
        });

        // Convert menuItems to menuData (MealsList)
        const menuData: MealsList = {};
        menuItems?.forEach((menuItem) => {
            const mealKey = menuItem.child('key').val();
            if (mealKey) {
                menuData[mealKey] = menuItem.val() as Meal;
            }
        });

        // Create MealLine object from current state
        const mealLine: MealLine = {
            key: food,
            quantity,
            quantitySide: quantitySide || undefined,
            quantityDessert: quantityDessert || undefined,
            recipient: recipient || '',
            schoolBreak: foodLine.schoolBreak || '',
            portionSize: foodLine.portionSize,
            isPackage: foodLine.isPackage,
            packagePrice: foodLine.packagePrice,
            orderItemRemark: foodLine.orderItemRemark
        };

        // Use the shared calculation function for single source of truth
        // Note: combosData is Combos type, but calculateMealLinePrice expects { [key: string]: Combo }
        // The backend handles this with (combosData as any), so we do the same here for consistency
        return calculateMealLinePrice(
            mealLine,
            meals,
            dailyMeals,
            recipients,
            staffDiscount,
            pricesExtra,
            sideDish,
            dessert,
            combosData as any,
            discountPercentage,
            menuData,
            !!modelConstants?.chargePackageExchangePrice
        );
    }, [sFoods, food, quantity, quantitySide, quantityDessert, foodItem, recipient, recipientPerson, sideDish, dessert, pricesExtra, foodLine.portionSize, foodLine.schoolBreak, foodLine.isPackage, foodLine.packagePrice, foodLine.orderItemRemark, combosData, discountPercentage, staffDiscount, dailyMealsList, menuItems, modelConstants, isSnack])

    const foodName = useMemo(() => {
        if (!food || !sFoods)
            return null;
        return getLangValue(foodItem?.name, lang) || sFoods.child(food)?.child('name')?.child(lang)?.val();
    }, [sFoods, food, foodItem, lang])

    const foodDescription = getLangValue(foodItem?.description, lang);
    const foodImageUrl = useStorageUrl(foodItem?.imgUrl);

    useEffect(() => {   // set starting quantity
        food && !+quantity && setQuantity(1);
    }, [food]);

    const canOrderMealMenu = foodItem?.courseType !== 'main' || (!!deadlineAt && canOrderMenuMealsForDay(date, modelConstants, deadlineAt));

    const localSnacks = useDatabase(snacksPath(currentCatering), EMPTY_FILTER, false, false);
    const snacksMaxLocal = useDatabase(localOrdersSnacksDatePath(date));


    const s_localSnack = isSnack ? localSnacks?.child(food) : null;

    // Only use local quantity limits
    const maxQuantityLocal = isSnack ? (s_localSnack?.child(KEY_MAX_QUANTITY_LOCAL)?.val() || 1000000) : 1000000;
    const snacksRemaining = maxQuantityLocal - (snacksMaxLocal?.child(food)?.val() ?? 0);


    // useEffect(() => {
    //     if (snacksRemaining < 0)
    //         snapshot.ref.child('quantity').set(foodLine.quantity + snacksRemaining).then(() => setDlgItemQuantityChanged(true))
    // }, [snacksMax, foodItem]);

    const [dlgDeleteFood, setDlgDeleteFood] = useState(false);

    const warnMainMenuLockedAfterDeadline = isSetMenu && !canOrderMealMenu && mainMenuWasOnPlacedOrder;

    const onDelete = () => {
        if (!warnMainMenuLockedAfterDeadline) {
            deleteOrder();
        } else {
            setDlgDeleteFood(true);
        }
    }

    const deleteOrder = () => {
        snapshot.ref.remove();
        setDlgDeleteFood(false);
    }

    const [dlgChangeMenuQuantity, setDlgChangeMenuQuantity] = useState(0);

    const changeQuantity = (newQuantity: number) => {
        setQuantity(newQuantity);
        if (quantitySide >= quantity)
            setQuantitySide(newQuantity);
        if (quantityDessert >= quantity)
            setQuantityDessert(newQuantity);
    }

    const onQuantitySelect = (newQuantity: number) => {
        if (!warnMainMenuLockedAfterDeadline) {
            changeQuantity(newQuantity);
        } else {
            setDlgChangeMenuQuantity(newQuantity);
        }
    }

    // const [dlgItemQuantityChanged, setDlgItemQuantityChanged] = useState(false);
    // const dlgItemQuantityChangedTitle = useLang('Αλλαγή Διαθεσιμότητας προϊόντων|Items Availability Change');
    // const dlgItemQuantityChangedMsg = useLang(`Το προϊόν ${foodItem.description} που έχεις επιλέξει δεν είναι διαθέσιμο σε αυτή την ποσότητα.|Change Order`);

    // Early return if sFoods is null (data not loaded)
    if (!sFoods) {
        return null;
    }

    const deleteButton = (className: string) => (
        <IconButton disabled={isFormDisabled || isSetDayMenu} className={`order-food-item__icon-btn order-food-item__delete-btn ${className}`} onClick={onDelete} aria-label={commonStrings.delete}>
            <DeleteOutlineIcon fontSize="small" />
        </IconButton>
    );

    const remarkButton = (className: string) => modelConstants?.allowCommentsPerFood ? (
        <IconButton disabled={isFormDisabled} className={`order-food-item__icon-btn order-food-item__remark-btn ${className}`} onClick={onOrderItemRemarkPressed} aria-label={orderStrings.orderItemRemarkAria}>
            <img style={{ width: '23px', height: '24px' }} src={hasOrderItemRemark ? "/img-assets/order-item-remark.svg" : "/img-assets/no-order-item-remark.svg"} />
        </IconButton>
    ) : null;

    const priceContent = price !== null && formatPriceEuro(Number(price), lang) || <span className='order-num-placeholder'>{orderStrings.pricePlaceholder}</span>;

    return <div className='order-food-item'>
        {dlgChangeMenuQuantity > 0 &&
            <YesNoDlg title={orderStrings.dlgChangeMainMenuTitle} msg={orderStrings.dlgChangeMainMenuMsg} yes={commonStrings.change} no={commonStrings.cancel} onNo={() => setDlgChangeMenuQuantity(0)} onYes={() => { changeQuantity(dlgChangeMenuQuantity); setDlgChangeMenuQuantity(0); }} />
        }
        {dlgDeleteFood &&
            <YesNoDlg title={orderStrings.dlgDeleteMainMenuTitle} msg={orderStrings.dlgDeleteMainMenuMsg} yes={commonStrings.delete} no={commonStrings.cancel} onNo={() => setDlgDeleteFood(false)} onYes={deleteOrder} />
        }
        {/* {dlgItemQuantityChanged &&
            <OkDlg title={dlgItemQuantityChangedTitle} msg={dlgItemQuantityChangedMsg} onClose={() => setDlgItemQuantityChanged(false)} />
        } */}

        {orderItemRemarkPopup
            &&
            <OrderPopup close='OK' onClose={() => setOrderItemRemarkPopup(false)} >
                <OrderItemRemarkPopUp snapshot={snapshot} />
            </OrderPopup>

        }
        <div className='order-food-item__main'>
            <div className='order-food-item__controls'>
                <QuantitySelect
                    variant="order"
                    className="order-food-item__qty"
                    id={`items-${snapshot.key}`}
                    value={quantity}
                    onChange={onQuantitySelect}
                    minQuantity={1}
                    maxQuantity={9}
                    disabled={isFormDisabled || isSetDayMenu}
                    showPlaceholderOption
                    placeholder={orderStrings.itemsPlaceholder}
                    isOptionDisabled={(optionQuantity) => (!canOrderMealMenu || snacksRemaining < optionQuantity) && quantity < optionQuantity}
                />
                {deleteButton('order-food-item__delete-desktop')}
                {remarkButton('order-food-item__remark-desktop')}
            </div>
            <div className={`order-food-item__details ${isFormDisabled ? 'disabled' : ''}`}>
                <div className={`order-food-item__title one-line ${isFormDisabled ? 'disabled' : ''}`}>
                    {foodName} {hasPortionSize && modelConstants?.allowChangePortions && shouldShowPortionSizeValue ? ' (' + portionSizeStr + ')' : ''}
                </div>
                {!!foodDescription && <div className='order-food-item__description'>{foodDescription}</div>}
                {hasMenuExtras &&
                    <MenuExtraChecklist meal={foodItem} date={date} quantitySide={quantitySide} quantityDessert={quantityDessert} sideDish={sideDish} dessert={dessert} />
                }
                {!isSetDayMenu &&
                    <div className='order-food-item__price-row'>
                        <div className={`order-food-item__price ${isFormDisabled ? 'disabled' : ''}`}>{priceContent}</div>
                        {remarkButton('order-food-item__remark-mobile')}
                        {deleteButton('order-food-item__delete-mobile')}
                    </div>
                }
            </div>
            {!isSetDayMenu &&
                <div className={`order-food-item__price order-food-item__price-desktop ${isFormDisabled ? 'disabled' : ''}`}>
                    {priceContent}
                </div>
            }
            {foodImageUrl &&
                <Box component="img" className='order-food-item__image' src={foodImageUrl} alt="" />
            }
        </div>
    </div>
}

type OrderLineProps = {
    date: string
    snapshot: DataSnapshot
    sFoods: DataSnapshot<Lookup<BaseFood>>
    dailyMealsList: DataSnapshot<Meal>[]
    isFormDisabled: boolean
    pricesExtra: PriceMealsExtra
    sideDish: Meal
    dessert: Meal
    isSetDayMenu: boolean
    discountPercentage: number
    /** Change order: this set-menu line existed on the committed placed order (post-deadline delete/qty warnings). */
    mainMenuWasOnPlacedOrder: boolean
}

function SchoolBreakDropdown({ OrderSnacksLists, OrderMealsLists, sSchoolBreaks, recipient, selected, selectedBreak, onChange, isFormDisabled }: SchoolBreakDropdownProps) {

    const [lang] = useContext(langContext);
    const orderStrings = useOrderStrings();

    let validBreaks = useList(sSchoolBreaks);
    const wholeList = [...OrderSnacksLists, ...OrderMealsLists];

    validBreaks = validBreaks.filter(br => {
        for (const o of wholeList) {
            if (o.child('recipient').val() === recipient) {
                if (o.child('schoolBreak').val() === (br.key)) {
                    return false;
                }
            }
        }
        return true;
    });
    const hasOneBreak = validBreaks?.length === 1;

    const changeSchoolBreak = !!selectedBreak.val() && !validBreaks.map(s => s.key).includes(selectedBreak.key + '');

    const onSelect = ({ target: { value } }) => {
        onChange(value);
    }

    function formatBreakTime(sBreak: DataSnapshot): string {
        const breakTitle = sBreak.child('description').child(lang).val();
        const timeStart = sBreak.child('timeStart').val();
        let br = !timeStart ? breakTitle : breakTitle + ': ' + sBreak.child('timeStart').val();
        if (!timeStart || !sBreak.child('timeEnd').val())
            return br;
        return br + ' - ' + sBreak.child('timeEnd').val();
    }
    return <TextField
        select
        size="medium"
        className="order-delivery-select"
        label={orderStrings.deliveryOptionLabel}
        value={selected ?? ''}
        onChange={(e) => onSelect({ target: { value: e.target.value } })}
        disabled={isFormDisabled || changeSchoolBreak || hasOneBreak}
        slotProps={{
            select: {
                displayEmpty: true,
            },
        }}
    >
        <MenuItem value="" disabled>{orderStrings.deliveryTimePlaceholder}</MenuItem>
        {selected && changeSchoolBreak && <MenuItem value={selectedBreak.key} disabled>{formatBreakTime(selectedBreak)}</MenuItem>}
        {validBreaks.map(sBreak => <MenuItem key={sBreak.key} value={sBreak.key}>{formatBreakTime(sBreak)}</MenuItem>)}
    </TextField>
}

type SchoolBreakDropdownProps = {
    OrderSnacksLists: DataSnapshot[]
    OrderMealsLists: DataSnapshot[]
    sSchoolBreaks: DataSnapshot
    recipient: string
    selected: string
    selectedBreak: DataSnapshot
    onChange?: (value: string) => void
    isFormDisabled: boolean
}

function OrderComplete({ onReturn, isOrderCompletion, date }: { onReturn: () => void, isOrderCompletion: boolean, date: string }) {

    const [schoolData] = useDatabaseVal<SchoolData>(SCHOOL_DATA_PATH);
    const [lang] = useContext(langContext);
    const orderStrings = useOrderStrings();

    // Format the date for display
    const formattedDate = getDateLocale(toDateFromServer(date), lang);

    return <>
        <div className='base_margin_setup'>
            {!isOrderCompletion && <>
                <div className='order-heading' >{orderStrings.orderCompleteCancelHeading}</div>
                <div className='order-remark' style={{ margin: '30px 0px 20px' }}>{orderStrings.orderCompleteCancelBeforeDate}<span style={{ fontWeight: 'bold', color: 'var(--ki_red_dark)', fontSize: '1.1em' }}>{formattedDate}</span>{orderStrings.orderCompleteCancelAfterDate}</div>
                <div className='order-remark'>{orderStrings.orderCompleteCreditsRefunded}</div>
            </>
            }
            {isOrderCompletion && <>
                <div className='order-heading'>{orderStrings.orderCompleteThankYou}</div>
                <div className='order-remark' style={{ margin: '30px 0px 20px' }}>{orderStrings.orderCompleteSuccessBeforeDate}<span style={{ fontWeight: 'bold', color: 'forestgreen', fontSize: '1.1em' }}>{formattedDate}</span>{orderStrings.orderCompleteSuccessAfterDate}</div>
                {/* <div className='order-remark'><Lang>Μπορείς να επεξεργαστείς την παραγγελία σου μέχρι τις |You may change your order until </Lang>{modelConstants?.orderTime}:00<Lang> της προηγούμενης ημέρας.| of the day before.</Lang></div> */}
            </>
            }
            <div className='order-remark'>{orderStrings.orderCompleteContactPrefix}{schoolData?.canteenTel}{orderStrings.orderCompleteContactBetween}{schoolData?.workingHours}</div>
            <div className='order-remark' style={{ margin: '32px' }}>{orderStrings.orderCompleteNewBalance}</div>

            <div className='balance-text-order-completed' >
                <Balance />
            </div>

            <button className={'credit-rectangle-filled order-completed-return-rectangle'} onClick={onReturn}>
                {orderStrings.orderCompleteReturnToOrders}
            </button>
        </div>
    </>
}


const disabledContext = createContext<boolean>(false);

const useDisabled = () => useContext(disabledContext);